import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminUuid } from '../../features/auth/authSlice';
// The portal's own token helpers. This direction is fine and matches how the
// Entrepreneur pages reach into b2b/auth for the same feature — the isolation
// rule is that portal/ must not import from routes/, not the reverse.
import {
  setPortalToken, setPortalImpersonator, getPortalImpersonator, portalFetch,
} from '../../portal/auth';
import {
  useGetClientUserQuery,
  useGetClientDocumentCatalogQuery,
  useSetClientDocumentsMutation,
  useUnpublishClientUserMutation,
  useDeactivateClientUserMutation,
  useReactivateClientUserMutation,
  useCreateClientLoginMutation,
  useUpdateClientUserMutation,
  useGetClientUploadDownloadUrlMutation,
  useReExtractClientUploadMutation,
  useSetClientUploadOwnerMutation,
  useLoadAdvisorsQuery,
  useListClientFirmsQuery,
  useListMyOfficesQuery,
  useGetClientAgreementQuery,
  useSendClientAgreementMutation,
  useGetAdvisorSigningUrlMutation,
  useLoadCompanyDirectoryQuery,
  useAddClientPointPersonMutation,
  useRemoveClientPointPersonMutation,
  useGetAdminConfigQuery,
  useGetClientFscLinkQuery,
  useSetClientFscLinkMutation,
  useClearClientFscLinkMutation,
  useImpersonateClientUserMutation,
  useGetClientDocumentsReminderQuery,
  useSendClientDocumentsReminderMutation,
  useGetClientSharedCatalogQuery,
  useListClientSharedDocumentsQuery,
  useShareClientDocumentMutation,
  useRevokeClientSharedDocumentMutation,
  useGetClientSharedDownloadUrlMutation,
  useGetClientSharedStreamUrlMutation,
  useStartSharedVideoMutation,
  useUploadSharedVideoPartMutation,
  useCompleteSharedVideoMutation,
  useAbortSharedVideoMutation,
  useGetClientSharedNotifyPreviewQuery,
  useSendClientSharedNotificationMutation,
} from '../../app/api';
import {
  useIsMobile, GEMSTONE_OPTIONS, gemstoneInfo, fetchAsBlobUrl, getRoleJSON,
  WORKFLOW_STAGE_OPTIONS, DEFAULT_WORKFLOW_STAGE,
} from '../utils';
import DocumentViewerModal from '../../components/DocumentViewerModal';
import { canPreviewFile, fileKindLabel, isVideoFile } from '../../components/fileKinds';
import FscHouseholdPicker, { formatUsd, formatDate } from './FscHouseholdPicker';
import FscDocumentTree from './FscDocumentTree';

// Sentinel type_key for the ad-hoc free-text request (mirrors the backend).
// Never appears in the document catalog.
const CUSTOM_KEY = 'custom';

const work = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  ink:       '#0a2947',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  hairlineStrong: '#c4cbd4',
  surface:   '#ffffff',
  canvas:    '#f3f5f8',
  green:     '#1a7f4e',
  red:       '#b00020',
};

function fullName(first, last, fallback) {
  const joined = `${first || ''} ${last || ''}`.trim();
  return joined || fallback || '—';
}

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(value);
      else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <button type="button" onClick={onClick} style={{
      background: copied ? work.green : work.tealBlue,
      color: 'white', border: 0, borderRadius: 4,
      padding: '0.35rem 0.7rem', fontWeight: 700, fontSize: '0.8rem',
      cursor: 'pointer', whiteSpace: 'nowrap',
    }}>
      <i className={`bi bi-${copied ? 'check-lg' : 'clipboard'} me-1`} />
      {copied ? 'Copied' : (label || 'Copy')}
    </button>
  );
}
CopyButton.propTypes = { value: PropTypes.string.isRequired, label: PropTypes.string };

function StatusPill({ isActive, hasPassword }) {
  let label = 'Draft';
  let bg = 'rgba(208,155,44,0.18)';
  let color = work.goldenrod;
  if (isActive) { label = 'Active'; bg = 'rgba(26,127,78,0.12)'; color = work.green; }
  else if (hasPassword) { label = 'Inactive'; bg = 'rgba(88,88,91,0.12)'; color = work.muted; }
  return (
    <span style={{
      background: bg, color,
      fontWeight: 700, fontSize: '0.7rem',
      textTransform: 'uppercase', letterSpacing: '0.5px',
      padding: '0.2rem 0.55rem', borderRadius: 4,
      whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}
StatusPill.propTypes = { isActive: PropTypes.bool, hasPassword: PropTypes.bool };

// Checkbox that opts into emailing the client their temp password + portal
// link when a login is created or reset. Shared shape with the New Client wizard.
// `ccNames` are the household's point people, who are cc'd on the login
// email like every other portal email. Named here because the cc is on the
// message the client reads: whoever ticks this box should know before it
// goes, not after a reply-all.
function EmailLoginToggle({ checked, onChange, ccNames = [] }) {
  return (
    <div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: work.deepBlue, cursor: 'pointer', userSelect: 'none' }}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span><i className="bi bi-envelope me-1" />Also email the client their login</span>
      </label>
      {checked && ccNames.length > 0 && (
        <div style={{ color: work.muted, fontSize: '0.7rem', marginTop: 2 }}>
          cc {ccNames.join(', ')}
        </div>
      )}
    </div>
  );
}
EmailLoginToggle.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  ccNames: PropTypes.arrayOf(PropTypes.string),
};

export default function ClientDetail() {
  const isMobile = useIsMobile();
  // The URL param is now the client's public_id (UUID), not the internal
  // sequential `id`. Variable name kept short to minimize churn in the
  // RTK Query calls below — every `id` here means "URL identifier."
  const { publicId } = useParams();
  const id = publicId;
  const location = useLocation();
  // Router-state passes the temp password when arriving from the New Client
  // wizard. We capture once into local state — refresh and it's gone.
  const [tempPassword, setTempPassword] = useState(location.state?.tempPassword || null);

  // `refetchClient` because this slice declares no `tagTypes` — invalidatesTags
  // is inert app-wide, so anything that edits the client has to re-read it
  // explicitly or the page keeps showing the old value.
  const { data: client, isLoading, isError, refetch: refetchClient } =
    useGetClientUserQuery({ id }, { skip: !id });

  // "Log in as" is WORK plus either ADMIN or WORK_ADMIN — the latter exists so
  // a CSA can be given client impersonation without the whole Admin console.
  // The backend is the authority (IMPERSONATE_CLIENT_ROLES in
  // routers/clients.py); this just keeps the button out of the way of
  // advisors who would only get a 403 from it.
  const currentUserUuid = useSelector(selectAdminUuid);
  const [canImpersonate, setCanImpersonate] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (currentUserUuid) {
      getRoleJSON(currentUserUuid).then(rj => {
        if (!cancelled) setCanImpersonate(Boolean(rj?.canImpersonateClients));
      });
    }
    return () => { cancelled = true; };
  }, [currentUserUuid]);

  const [impersonateClient, { isLoading: isImpersonating }] = useImpersonateClientUserMutation();
  const { data: catalog = [] } = useGetClientDocumentCatalogQuery();
  const [setDocs, { isLoading: isSavingDocs }] = useSetClientDocumentsMutation();
  const [unpublishClient, { isLoading: isUnpublishing }] = useUnpublishClientUserMutation();
  const [deactivateClient, { isLoading: isDeactivating }] = useDeactivateClientUserMutation();
  const [reactivateClient, { isLoading: isReactivating }] = useReactivateClientUserMutation();
  const [createLogin, { isLoading: isCreatingLogin }] = useCreateClientLoginMutation();
  const [updateClient, { isLoading: isSavingEdit }] = useUpdateClientUserMutation();

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '', last_name: '', email: '',
    workflow_status: DEFAULT_WORKFLOW_STAGE, firm_id: '', office_fsc_id: '',
  });
  const { data: firmsData } = useListClientFirmsQuery();
  const firmOptions = firmsData?.firms || [];
  // Offices this user may move the client to — never the full list, so a
  // reassignment can't push a household somewhere the mover can't follow it.
  const { data: myOffices } = useListMyOfficesQuery();
  const officeOptions = myOffices?.offices || [];
  const [editError, setEditError] = useState(null);

  const [selected, setSelected] = useState(new Set());
  // Ad-hoc custom requests (type_key='custom'). A client can have several, each
  // its own free-text ask. Rows carry the DB id (when saved) so edits keep
  // their upload history, plus a stable `uid` for React keys on unsaved rows.
  const [customList, setCustomList] = useState([]);
  const customUid = useRef(0);
  const newCustomRow = (label = '', id = null) => ({ id, label, uid: `c${customUid.current++}` });
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);

  // Log in as this client: mint a portal token for them, mark the browser as
  // an impersonation session, and hand the browser to the portal. The
  // employee's own intranet token is a different key in localStorage and is
  // left alone, so "Return to the hub" just discards the portal one.
  const handleImpersonate = async () => {
    const who = fullName(client?.first_name, client?.last_name, client?.email);
    if (!window.confirm(
      `Log in as ${who}? You'll see their client portal exactly as they do, and anything `
      + 'you do there is done as them. Use "Return to the hub" to come back.'
    )) return;
    setError(null);
    try {
      // Jumping straight from one client's portal to another's would leave the
      // first impersonation_audit row open forever, so close it first. Uses the
      // OLD token, which is still the stored one at this point.
      if (getPortalImpersonator()) {
        try { await portalFetch('/api/portal/impersonate/return', { method: 'POST' }); }
        catch { /* best effort — a stale token shouldn't block the new session */ }
      }
      const res = await impersonateClient({ id }).unwrap();
      setPortalToken(res.token);
      setPortalImpersonator({
        target_email: res.target_email,
        target_name: res.target_name,
        client_public_id: id,
      });
      // A full navigation, not react-router: the portal is a separate
      // top-level app (see index.js) and has to mount fresh on the new token.
      window.location.assign('/portal/dashboard');
    } catch (e) {
      setError(e?.data?.detail || 'Could not log in as this client.');
    }
  };

  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [deactivateError, setDeactivateError] = useState(null);
  // Whether to also email the client their temp password + portal link when
  // creating/resetting the login. Default on. `loginEmail` records the outcome
  // (from location state when arriving from the wizard, else from the response).
  const [emailClient, setEmailClient] = useState(true);
  const [loginEmail, setLoginEmail] = useState(location.state?.loginEmail || null);

  // Dev-mode toggle reveals tools we don't want visible during a demo
  // (e.g. the Re-extract button on each upload row). Persisted in
  // sessionStorage so it survives navigation within a session but
  // resets when the tab closes — no risk of carrying it into a fresh
  // demo the next morning.
  const [devMode, setDevMode] = useState(() => {
    try { return window.sessionStorage.getItem('pca.devMode') === '1'; }
    catch { return false; }
  });
  const toggleDevMode = () => {
    setDevMode(prev => {
      const next = !prev;
      try { window.sessionStorage.setItem('pca.devMode', next ? '1' : '0'); }
      catch { /* sessionStorage disabled — fall back to in-memory only */ }
      return next;
    });
  };

  const [hydratedFor, setHydratedFor] = useState(null);
  useEffect(() => {
    if (!client) return;
    if (hydratedFor === client.id) return;
    const reqs = client.requested_documents || [];
    // The 'custom' row is free text, not a catalog checkbox — keep it out of
    // the checkbox Set and hydrate the custom editor from it instead.
    setSelected(new Set(reqs.filter(d => d.type_key !== CUSTOM_KEY).map(d => d.type_key)));
    setCustomList(
      reqs.filter(d => d.type_key === CUSTOM_KEY).map(d => newCustomRow(d.custom_label || '', d.id)),
    );
    setDirty(false);
    setHydratedFor(client.id);
  }, [client, hydratedFor]);

  const toggle = (key) => {
    setError(null);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setDirty(true);
  };

  const addCustom = () => {
    setError(null);
    setCustomList(prev => [...prev, newCustomRow()]);
    setDirty(true);
  };
  const changeCustom = (uid, label) => {
    setError(null);
    setCustomList(prev => prev.map(c => (c.uid === uid ? { ...c, label } : c)));
    setDirty(true);
  };
  const removeCustom = (uid) => {
    setError(null);
    setCustomList(prev => prev.filter(c => c.uid !== uid));
    setDirty(true);
  };

  // Send every custom row (id + trimmed label); the backend drops blanks and
  // reconciles by id, so removed/blank rows are deleted and edits keep history.
  const customRequestsPayload = () =>
    customList.map(c => ({ id: c.id ?? undefined, label: c.label.trim() }));
  const hasCustom = customList.some(c => c.label.trim().length > 0);

  const saveSelections = async () => {
    setError(null);
    try {
      await setDocs({ id, type_keys: Array.from(selected), custom_requests: customRequestsPayload() }).unwrap();
      setDirty(false);
    } catch (e) {
      setError(e?.data?.detail || 'Could not save the document list.');
    }
  };

  const handleCreateLogin = async () => {
    setError(null);
    if (dirty) {
      try {
        await setDocs({ id, type_keys: Array.from(selected), custom_requests: customRequestsPayload() }).unwrap();
        setDirty(false);
      } catch (e) {
        setError(e?.data?.detail || 'Could not save the document list first.');
        return;
      }
    }
    try {
      const res = await createLogin({ id, send_email: emailClient }).unwrap();
      setTempPassword(res.temp_password);
      setLoginEmail(emailClient ? { sent: res.email_sent, error: res.email_error, address: res.email } : null);
      setConfirmReset(false);
    } catch (e) {
      setError(e?.data?.detail || 'Could not create the login.');
    }
  };

  const openEdit = () => {
    setEditError(null);
    setEditForm({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      email: client.email || '',
      workflow_status: client.workflow_status || DEFAULT_WORKFLOW_STAGE,
      firm_id: client.firm_id || '',
      office_fsc_id: client.office_fsc_id || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setEditError(null);
    const f = (editForm.first_name || '').trim();
    const l = (editForm.last_name || '').trim();
    const e = (editForm.email || '').trim();
    // Without an email we still need at least a name to identify the row.
    if (!e && !f && !l) {
      setEditError('Provide an email, or a first/last name for a name-only partner.');
      return;
    }
    try {
      await updateClient({
        id,
        first_name: f,
        last_name: l,
        // Pass an explicit empty string when the operator cleared the email
        // so the backend can distinguish "leave alone" (omit) from "clear".
        email: e,
        workflow_status: editForm.workflow_status,
        // Number for a real firm; null clears it (backend treats null/0 as unassign).
        firm_id: editForm.firm_id ? Number(editForm.firm_id) : null,
        // Only when it actually changed: the PATCH rewrites every member of
        // the household and stamps office_source='manual', so resending the
        // same value would overwrite an 'aum_link' provenance with a guess.
        ...(editForm.office_fsc_id && editForm.office_fsc_id !== (client.office_fsc_id || '')
          ? { office_fsc_id: editForm.office_fsc_id }
          : {}),
      }).unwrap();
      setEditOpen(false);
    } catch (err) {
      setEditError(err?.data?.detail || 'Could not save changes.');
    }
  };

  const handleUnpublish = async () => {
    setError(null);
    try {
      await unpublishClient({ id }).unwrap();
    } catch (e) {
      setError(e?.data?.detail || 'Could not unpublish.');
    }
  };

  const handleDeactivate = async () => {
    setDeactivateError(null);
    try {
      await deactivateClient({ id }).unwrap();
      setConfirmDeactivate(false);
    } catch (e) {
      setDeactivateError(e?.data?.detail || 'Could not deactivate that client.');
    }
  };

  const handleReactivate = async () => {
    setDeactivateError(null);
    try {
      await reactivateClient({ id }).unwrap();
    } catch (e) {
      setDeactivateError(e?.data?.detail || 'Could not reactivate that client.');
    }
  };

  const totalItems = useMemo(
    () => catalog.reduce((sum, cat) => sum + (cat.items?.length || 0), 0),
    [catalog],
  );

  if (isError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: work.red }}>
        Client not found. <Link to="/work/clients">Back to portal clients.</Link>
      </div>
    );
  }
  if (isLoading || !client) {
    return (
      <div style={{ background: work.canvas, minHeight: '100vh', padding: '2rem', textAlign: 'center', color: work.muted }}>
        Loading…
      </div>
    );
  }

  const hasPassword = !!client.password_hash || client.is_active || !!client.published_at;
  const isActive = !!client.is_active;
  // Portal email to this client is cc'd to these people, except the sign-in
  // code and password-reset emails (see _point_people_cc in routers/clients.py). Only used to label the send
  // controls - the backend reads the list itself, so nothing here decides
  // who is actually copied.
  const pointPeopleNames = (client.point_people || [])
    .filter(p => p.email)
    .map(p => p.name || p.email);
  const partner = (client.household_members || []).find(m => m.id !== client.id) || null;

  return (
    <div style={{ background: work.canvas, minHeight: '100vh', padding: isMobile ? '1rem' : '1.5rem 1.75rem', color: work.ink }}>

      <Link to="/work/clients" style={{ color: work.tealBlue, fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}>
        <i className="bi bi-chevron-left" /> Portal clients
      </Link>

      {/* Header */}
      <div style={{
        background: work.surface, border: `1px solid ${work.hairline}`,
        borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6,
        padding: isMobile ? '1.1rem 1.1rem' : '1.4rem 1.6rem',
        marginTop: '0.75rem', marginBottom: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap' }}>
          <div style={{
            background: work.deepBlue, color: 'white', width: 44, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6, flexShrink: 0,
          }}>
            <i className="bi bi-person-fill" style={{ fontSize: '1.3rem' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: 2 }}>
              <h3 style={{ color: work.ink, fontWeight: 800, margin: 0, fontSize: isMobile ? '1.2rem' : '1.4rem', lineHeight: 1.15 }}>
                {fullName(client.first_name, client.last_name, client.email)}
              </h3>
              <StatusPill isActive={isActive} hasPassword={hasPassword} />
              {client.is_test && (
                <span
                  title="Test household — not a real client. Documents go to the deletable beta-test bucket."
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    background: 'rgba(208,155,44,0.18)', color: work.goldenrod,
                    fontWeight: 700, fontSize: '0.65rem',
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                    padding: '0.15rem 0.5rem', borderRadius: 4, whiteSpace: 'nowrap',
                  }}
                >
                  <i className="bi bi-flask" /> Test
                </span>
              )}
              {client.deactivated_at && (
                <span
                  title="Deactivated — can no longer log in. Their document history is preserved for compliance."
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    background: 'rgba(176,0,32,0.1)', color: work.red,
                    fontWeight: 700, fontSize: '0.65rem',
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                    padding: '0.15rem 0.5rem', borderRadius: 4, whiteSpace: 'nowrap',
                  }}
                >
                  <i className="bi bi-slash-circle" /> Deactivated
                </span>
              )}
            </div>
            <div style={{ color: work.muted, fontSize: '0.85rem' }}>
              {client.email ? (
                <><i className="bi bi-envelope me-1" />{client.email}</>
              ) : (
                <span style={{ fontStyle: 'italic' }}>
                  <i className="bi bi-person-dash me-1" />Name-only partner — no email, no login
                </span>
              )}
              {client.hubspot_contact_id && (
                <span style={{ marginLeft: '1rem' }}>
                  <i className="bi bi-link-45deg me-1" />HubSpot ID {client.hubspot_contact_id}
                </span>
              )}
            </div>
            {partner && (
              <div style={{ color: work.muted, fontSize: '0.85rem', marginTop: 4 }}>
                <i className="bi bi-house-heart me-1" />
                Household with{' '}
                <Link to={`/work/clients/${partner.public_id}`} style={{ color: work.tealBlue, fontWeight: 600, textDecoration: 'none' }}>
                  {fullName(partner.first_name, partner.last_name, partner.email)}
                </Link>
              </div>
            )}
            <AdvisorPicker client={client} updateClient={updateClient} />
            <GemstonePicker client={client} updateClient={updateClient} />
            <StagePicker client={client} updateClient={updateClient} onSaved={refetchClient} />
            <PointPeopleEditor client={client} />
          </div>
        </div>

        {/* Actions, on their own full-width row UNDER the details rather than
            beside them. As a third column they wrapped into the middle of the
            identity block on a narrow screen - "Confirm reset" landing between
            GEMSTONE and its dropdown - because the details column keeps its
            own width and the buttons had nowhere sensible to go. */}
        <div style={{
          display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center',
          marginTop: '1rem', paddingTop: '0.9rem',
          borderTop: `1px solid ${work.hairline}`,
        }}>
            <button
              type="button"
              onClick={openEdit}
              style={{
                background: 'transparent', color: work.deepBlue,
                border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                padding: '0.5rem 0.95rem', fontWeight: 700, fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              <i className="bi bi-pencil me-1" /> Edit
            </button>
            {/* Log in as.
                THE ROLE decides whether this exists at all: someone without
                ADMIN/WORK_ADMIN never sees it, on any client, so there is no
                per-client surprise to explain and a permanently dead button
                would just advertise a permission they don't have.
                THE CLIENT'S STATE decides whether it works, and that DOES vary
                client to client — which is the part that reads as a bug when
                the button silently disappears. So for someone who holds the
                role it always renders, disabled with the actual reason.
                Draft clients became an ordinary resting state the day the
                wizard stopped requiring a document list, which is what made
                this worth spelling out. */}
            {canImpersonate && (() => {
              // Only deactivation blocks now. A Draft client is the case this
              // is MOST useful for — walking the portal before you invite
              // someone is how you find out what they'll land on.
              const blocked = client.deactivated_at
                ? 'This client is deactivated - reactivate them to open their portal.'
                : null;
              return (
                <button
                  type="button"
                  onClick={handleImpersonate}
                  disabled={isImpersonating || !!blocked}
                  title={blocked || (hasPassword
                    ? "Open this client's portal as them"
                    : "Preview their portal before they have a login")}
                  style={{
                    background: 'transparent',
                    color: blocked ? work.muted : work.deepBlue,
                    border: `1px solid ${blocked ? work.hairline : work.hairlineStrong}`,
                    borderRadius: 4,
                    padding: '0.5rem 0.95rem', fontWeight: 700, fontSize: '0.85rem',
                    cursor: blocked ? 'not-allowed' : (isImpersonating ? 'wait' : 'pointer'),
                  }}
                >
                  <i className="bi bi-incognito me-1" />
                  {isImpersonating ? 'Opening…' : 'Log in as'}
                </button>
              );
            })()}
            {!client.email ? null : !hasPassword ? (
              <>
                <EmailLoginToggle checked={emailClient} onChange={setEmailClient} ccNames={pointPeopleNames} />
                {/* No longer gated on having requested something. A household
                    can be set up purely to RECEIVE documents from us, and
                    withholding their login until someone ticks a checkbox
                    they don't need would block exactly that case. */}
                <button type="button" onClick={handleCreateLogin} disabled={isCreatingLogin} style={{
                  background: work.green, color: 'white',
                  border: 0, borderRadius: 4, padding: '0.5rem 0.95rem',
                  fontWeight: 700, fontSize: '0.85rem',
                  cursor: isCreatingLogin ? 'wait' : 'pointer',
                }}>
                  <i className="bi bi-key me-1" />
                  {isCreatingLogin ? 'Creating…' : 'Create login'}
                </button>
              </>
            ) : (
              <>
                {confirmReset ? (
                  <>
                    <EmailLoginToggle checked={emailClient} onChange={setEmailClient} ccNames={pointPeopleNames} />
                    <button type="button" onClick={handleCreateLogin} disabled={isCreatingLogin} style={{
                      background: work.red, color: 'white', border: 0, borderRadius: 4,
                      padding: '0.5rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                    }}>
                      {isCreatingLogin ? 'Resetting…' : 'Confirm reset'}
                    </button>
                    <button type="button" onClick={() => setConfirmReset(false)} style={{
                      background: 'transparent', color: work.muted,
                      border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                      padding: '0.5rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                    }}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => setConfirmReset(true)} title="Generates a fresh temp password and replaces the old one" style={{
                      background: 'transparent', color: work.deepBlue,
                      border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                      padding: '0.5rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                    }}>
                      <i className="bi bi-arrow-clockwise me-1" /> Reset login
                    </button>
                    {isActive && (
                      <button type="button" onClick={handleUnpublish} disabled={isUnpublishing} title="Moves the client back to Draft — hides the portal login until re-published. Does not affect their document history." style={{
                        background: 'transparent', color: work.muted,
                        border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                        padding: '0.5rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                      }}>
                        {isUnpublishing ? 'Unpublishing…' : 'Unpublish'}
                      </button>
                    )}
                  </>
                )}
              </>
            )}
            {client.deactivated_at ? (
              <button type="button" onClick={handleReactivate} disabled={isReactivating} style={{
                background: 'transparent', color: work.tealBlue,
                border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                padding: '0.5rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
              }}>
                <i className="bi bi-arrow-counterclockwise me-1" />
                {isReactivating ? 'Reactivating…' : 'Reactivate client'}
              </button>
            ) : confirmDeactivate ? (
              <>
                <button type="button" onClick={handleDeactivate} disabled={isDeactivating} style={{
                  background: work.red, color: 'white', border: 0, borderRadius: 4,
                  padding: '0.5rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                }}>
                  {isDeactivating ? 'Deactivating…' : 'Confirm deactivate'}
                </button>
                <button type="button" onClick={() => setConfirmDeactivate(false)} style={{
                  background: 'transparent', color: work.muted,
                  border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                  padding: '0.5rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                }}>
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDeactivate(true)}
                title="Blocks login and hides them from the default Clients list. Their document history is kept for compliance — nothing is deleted."
                style={{
                  background: 'transparent', color: work.red,
                  border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                  padding: '0.5rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                }}
              >
                <i className="bi bi-slash-circle me-1" /> Deactivate client
              </button>
            )}
          </div>
        {deactivateError && (
          <div style={{ width: '100%', color: work.red, fontSize: '0.8rem', marginTop: '0.5rem' }}>
            {deactivateError}
          </div>
        )}
      </div>

      {/* Temp password (only shown the first time / right after reset) */}
      {tempPassword && (
        <div style={{
          background: 'rgba(26,127,78,0.06)', border: `1px solid ${work.green}`,
          borderLeft: `4px solid ${work.green}`, borderRadius: 6,
          padding: '1rem 1.1rem', marginBottom: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <i className="bi bi-check-circle-fill" style={{ color: work.green, fontSize: '1.1rem' }} />
            <div style={{ fontWeight: 800, color: work.ink, fontSize: '0.95rem' }}>
              Login ready — copy the temp password before you leave this page
            </div>
            <button type="button" onClick={() => setTempPassword(null)} aria-label="Dismiss" style={{ marginLeft: 'auto', background: 'transparent', border: 0, color: work.muted, cursor: 'pointer' }}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr auto', gap: '0.4rem 0.85rem', alignItems: 'center', fontSize: '0.875rem' }}>
            <span style={{ color: work.muted, fontWeight: 600 }}>Username</span>
            <code style={{ background: 'white', border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.3rem 0.5rem', color: work.ink }}>
              {client.email}
            </code>
            <CopyButton value={client.email} label="Copy email" />
            <span style={{ color: work.muted, fontWeight: 600 }}>Temp password</span>
            <code style={{ background: 'white', border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.3rem 0.5rem', color: work.ink, letterSpacing: '0.05em', fontWeight: 700 }}>
              {tempPassword}
            </code>
            <CopyButton value={tempPassword} label="Copy password" />
          </div>
          {loginEmail && (
            <p style={{ fontSize: '0.8rem', margin: '0.65rem 0 0', color: loginEmail.sent ? work.green : work.goldenrod, fontWeight: 600 }}>
              <i className={`bi bi-${loginEmail.sent ? 'envelope-check' : 'exclamation-triangle'} me-1`} />
              {loginEmail.sent
                ? `Emailed to ${loginEmail.address}.`
                : `Couldn't email the client (${loginEmail.error || 'send failed'}) — share the password manually.`}
            </p>
          )}
          <p style={{ fontSize: '0.78rem', color: work.muted, margin: '0.65rem 0 0' }}>
            <i className="bi bi-info-circle me-1" />
            Only shown once. Reload this page and it&apos;s gone — we keep only the bcrypt hash.
          </p>
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(176,0,32,0.06)', border: `1px solid ${work.red}`, color: work.red, borderRadius: 6, padding: '0.55rem 0.85rem', fontSize: '0.875rem', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Which Salesforce/Orion client this portal household is */}
      <AumClientCard client={client} isMobile={isMobile} />

      {/* DocuSign FP Consulting Agreement (fee-bearing clients only) */}
      <AgreementSection client={client} />

      {/* Files received from the client (with Textract summaries) */}
      <ReceivedFiles client={client} catalog={catalog} devMode={devMode} />

      {/* The other direction: what we have sent them, and who has opened it */}
      <SharedFiles client={client} />

      {/* The same client's documents already on file in Salesforce, once
          they're linked to an AUM client */}
      <SalesforceDocuments client={client} isMobile={isMobile} />

      {/* Document catalog.
          NO `overflow: hidden` here, unlike the other cards on this page: the
          header holds RemindClientButton, whose confirm popover is positioned
          absolutely and taller than the header. Clipping the card clipped the
          popover in half, so the advisor couldn't read what they were about to
          send a client. Nothing in this card has a background that reaches the
          rounded corners, so there is nothing for the clip to have been doing. */}
      <div style={{
        background: work.surface, border: `1px solid ${work.hairline}`,
        borderRadius: 6, marginBottom: '1rem',
      }}>
        <div style={{
          padding: '0.85rem 1rem', borderBottom: `1px solid ${work.hairline}`,
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: work.ink, fontSize: '0.95rem' }}>
              Requested documents
            </div>
            <div style={{ color: work.muted, fontSize: '0.8rem', marginTop: 2 }}>
              {partner
                ? <>This list is for <strong>{client.first_name || client.email}</strong>. <Link to={`/work/clients/${partner.public_id}`} style={{ color: work.tealBlue, textDecoration: 'none' }}>Manage {partner.first_name || partner.email}&apos;s documents →</Link></>
                : <><strong>{selected.size}</strong> selected of <strong>{totalItems}</strong> in catalog.</>
              }
            </div>
          </div>
          <RemindClientButton client={client} dirty={dirty} />
          <button type="button" onClick={saveSelections} disabled={!dirty || isSavingDocs} style={{
            background: dirty ? work.tealBlue : work.hairline,
            color: dirty ? 'white' : work.muted,
            border: 0, borderRadius: 4,
            padding: '0.45rem 0.9rem', fontWeight: 700, fontSize: '0.82rem',
            cursor: dirty ? 'pointer' : 'default',
          }}>
            {isSavingDocs ? 'Saving…' : (dirty ? 'Save changes' : 'Saved')}
          </button>
        </div>

        {/* Custom requests — free-text asks that aren't in the catalog. Add as
            many as needed; each becomes its own tracked request with its own
            uploads. Sits above the checklist so ad-hoc asks are reached first. */}
        <div style={{
          padding: '0.9rem 1rem', borderBottom: `1px solid ${work.hairline}`,
          background: hasCustom ? 'rgba(208,155,44,0.06)' : work.surface,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
            <span>
              <span style={{ fontWeight: 700, color: work.ink, fontSize: '0.9rem' }}>
                Custom requests
              </span>
              <span style={{ display: 'block', color: work.muted, fontSize: '0.8rem', marginTop: 1 }}>
                Ask for anything not in the catalog below — add one row per document.
              </span>
            </span>
            <button type="button" onClick={addCustom} style={{
              flexShrink: 0, background: 'transparent', border: `1px solid ${work.hairlineStrong}`,
              borderRadius: 4, padding: '0.35rem 0.6rem', fontSize: '0.8rem', fontWeight: 700,
              color: work.deepBlue, cursor: 'pointer',
            }}>
              <i className="bi bi-plus-lg me-1" />Add request
            </button>
          </div>
          {customList.length > 0 && (
            <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {customList.map((c, i) => (
                <div key={c.uid} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <textarea
                    value={c.label}
                    onChange={(e) => changeCustom(c.uid, e.target.value)}
                    placeholder={i === 0 ? 'e.g. Trust amendment signed in 2023' : 'Describe another document to upload'}
                    rows={2}
                    style={{
                      flex: 1, border: `1px solid ${work.hairline}`, borderRadius: 4,
                      padding: '0.5rem 0.7rem', fontSize: '0.875rem',
                      color: work.ink, outline: 'none', resize: 'vertical', fontFamily: 'inherit',
                    }}
                  />
                  <button type="button" onClick={() => removeCustom(c.uid)} title="Remove this request" style={{
                    flexShrink: 0, background: 'transparent', border: `1px solid ${work.hairline}`,
                    borderRadius: 4, padding: '0.5rem 0.6rem', color: work.red, cursor: 'pointer',
                  }}>
                    <i className="bi bi-trash" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {catalog.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: work.muted }}>Loading catalog…</div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 0,
          }}>
            {catalog.map(cat => (
              <div key={cat.category} style={{ borderRight: isMobile ? 'none' : `1px solid ${work.hairline}`, borderBottom: `1px solid ${work.hairline}`, padding: '0.85rem 1rem' }}>
                <div style={{ color: work.tealBlue, fontWeight: 800, fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                  {cat.label}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {(cat.items || []).map(item => {
                    const checked = selected.has(item.key);
                    return (
                      <label key={item.key} style={{
                        display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                        cursor: 'pointer', padding: '0.25rem 0.3rem', borderRadius: 4,
                        background: checked ? 'rgba(0,105,166,0.06)' : 'transparent',
                      }}>
                        <input type="checkbox" checked={checked} onChange={() => toggle(item.key)} style={{ marginTop: 3 }} />
                        <span style={{ color: work.ink, fontSize: '0.85rem', lineHeight: 1.35 }}>
                          {item.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p style={{ color: work.muted, fontSize: '0.78rem', margin: 0 }}>
        <i className="bi bi-info-circle me-1" />
        {!client.email
          ? <>This is a name-only partner — they have no login of their own. Their documents are tracked here, but the primary household member uploads on their behalf.</>
          : !hasPassword
            ? <>Creating a login will generate a temp password and activate the account so the client can sign in via <code>/portal/login</code>. You can&apos;t create a login until at least one document is selected.</>
            : isActive
              ? <>Login is active. Use <strong>Reset login</strong> if the client lost their temp password, or <strong>Unpublish</strong> to block sign-in without losing data.</>
              : <>Account is in Draft. Use <strong>Reset login</strong> to issue a new temp password and re-publish.</>
        }
      </p>

      {/* Edit modal */}
      {editOpen && (
        <div
          onClick={() => !isSavingEdit && setEditOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.55)',
            zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 6, width: '100%', maxWidth: 520, boxShadow: '0 12px 40px rgba(10,41,71,0.3)', overflow: 'hidden' }}
          >
            <div style={{ padding: '1.1rem 1.25rem', borderBottom: `1px solid ${work.hairline}`, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <i className="bi bi-pencil-square" style={{ color: work.deepBlue, fontSize: '1.1rem' }} />
              <h5 style={{ color: work.ink, fontWeight: 800, margin: 0, fontSize: '1.05rem' }}>
                Edit client
              </h5>
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>First name</span>
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm(f => ({ ...f, first_name: e.target.value }))}
                    disabled={isSavingEdit}
                    style={{
                      width: '100%', border: `1px solid ${work.hairline}`, borderRadius: 4,
                      padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink, outline: 'none',
                    }}
                  />
                </label>
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Last name</span>
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm(f => ({ ...f, last_name: e.target.value }))}
                    disabled={isSavingEdit}
                    style={{
                      width: '100%', border: `1px solid ${work.hairline}`, borderRadius: 4,
                      padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink, outline: 'none',
                    }}
                  />
                </label>
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Email</span>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                    disabled={isSavingEdit}
                    placeholder="Leave blank for a name-only partner"
                    style={{
                      width: '100%', border: `1px solid ${work.hairline}`, borderRadius: 4,
                      padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink, outline: 'none',
                    }}
                  />
                  <p style={{ color: work.muted, fontSize: '0.72rem', margin: '0.3rem 0 0' }}>
                    Clearing the email turns this into a name-only partner and wipes any existing login. Adding an email later lets you create a login from the Create-login button.
                  </p>
                </label>
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Workflow stage</span>
                  <select
                    value={editForm.workflow_status}
                    onChange={(e) => setEditForm(f => ({ ...f, workflow_status: e.target.value }))}
                    disabled={isSavingEdit}
                    style={{
                      width: '100%', border: `1px solid ${work.hairline}`, borderRadius: 4,
                      padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink, outline: 'none',
                      background: 'white',
                    }}
                  >
                    {WORKFLOW_STAGE_OPTIONS.map(s => (
                      <option key={s.key} value={s.key}>{`${s.label} - ${s.hint.toLowerCase()}`}</option>
                    ))}
                  </select>
                  <p style={{ color: work.muted, fontSize: '0.72rem', margin: '0.3rem 0 0' }}>
                    Drives the four-stage progress indicator on the portal dashboard.
                  </p>
                </label>
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Firm</span>
                  <select
                    value={editForm.firm_id}
                    onChange={(e) => setEditForm(f => ({ ...f, firm_id: e.target.value }))}
                    disabled={isSavingEdit}
                    style={{
                      width: '100%', border: `1px solid ${work.hairline}`, borderRadius: 4,
                      padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink, outline: 'none',
                      background: 'white',
                    }}
                  >
                    <option value="">Unassigned</option>
                    {firmOptions.map((fm) => <option key={fm.id} value={fm.id}>{fm.name}</option>)}
                  </select>
                  <p style={{ color: work.muted, fontSize: '0.72rem', margin: '0.3rem 0 0' }}>
                    Controls document security: employees see internal &amp; unassigned clients; a B2B firm sees only its own clients&apos; documents.
                  </p>
                </label>
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Office</span>
                  <select
                    value={editForm.office_fsc_id}
                    onChange={(e) => setEditForm(f => ({ ...f, office_fsc_id: e.target.value }))}
                    disabled={isSavingEdit || officeOptions.length === 0}
                    style={{
                      width: '100%', border: `1px solid ${work.hairline}`, borderRadius: 4,
                      padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink, outline: 'none',
                      background: 'white',
                    }}
                  >
                    {/* Once it has an office there is no way back to none:
                        clearing one hides the client from everyone but
                        full-access users, and the backend refuses it. */}
                    {!client.office_fsc_id && <option value="">Not assigned</option>}
                    {officeOptions.map((o) => <option key={o.fsc_id} value={o.fsc_id}>{o.name}</option>)}
                    {/* The current office when it isn't one of this user's own
                        — a full-access user editing another office's client.
                        Without it the select would show a different office as
                        selected and quietly move the household on save. */}
                    {client.office_fsc_id && !officeOptions.some(o => o.fsc_id === client.office_fsc_id) && (
                      <option value={client.office_fsc_id}>{client.office_name || client.office_fsc_id}</option>
                    )}
                  </select>
                  <p style={{ color: work.muted, fontSize: '0.72rem', margin: '0.3rem 0 0' }}>
                    Moves the whole household, and decides which colleagues can see it.
                    {client.office_source === 'aum_link' && ' Currently taken from the linked AUM household.'}
                  </p>
                </label>
              </div>
              {editError && (
                <div style={{ background: 'rgba(176,0,32,0.06)', border: `1px solid ${work.red}`, color: work.red, borderRadius: 4, padding: '0.45rem 0.7rem', fontSize: '0.8rem', marginTop: '0.85rem' }}>
                  {editError}
                </div>
              )}
            </div>
            <div style={{ padding: '0.85rem 1.25rem', borderTop: `1px solid ${work.hairline}`, display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                disabled={isSavingEdit}
                style={{
                  background: 'transparent', color: work.deepBlue,
                  border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                  padding: '0.45rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={isSavingEdit}
                style={{
                  background: work.tealBlue, color: 'white', border: 0, borderRadius: 4,
                  padding: '0.45rem 0.95rem', fontWeight: 700, fontSize: '0.85rem',
                  cursor: isSavingEdit ? 'wait' : 'pointer',
                }}
              >
                {isSavingEdit ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Demo-safe dev toggle. Left-aligned so it doesn't collide with
          the site-feedback widget anchored to the bottom-right. A single
          click reveals per-upload "Re-extract" buttons while we tune
          Textract heuristics. */}
      <div style={{
        marginTop: '2rem',
        textAlign: 'left',
        fontSize: '0.65rem',
        color: work.muted,
        opacity: 0.5,
      }}>
        <button
          type="button"
          onClick={toggleDevMode}
          style={{
            background: 'transparent', border: 0, padding: 0,
            color: 'inherit', fontSize: 'inherit', cursor: 'pointer',
            textDecoration: 'underline', letterSpacing: '0.04em',
          }}
        >
          {devMode ? 'dev tools: on (click to hide)' : 'dev'}
        </button>
      </div>
    </div>
  );
}

// ─── Advisor picker ─────────────────────────────────────────────────────────
//
// Sits under the client name in the header. The primary advisor is chosen from
// a dropdown of company-directory people flagged `is_advisor`; a second advisor
// can be added for households served by a team (not the common case, so it's
// behind an "Add second advisor" affordance). Each change PATCHes immediately —
// there's no separate save button, matching the inline-edit feel of the page.
function AdvisorPicker({ client, updateClient }) {
  const { data: advisors = [] } = useLoadAdvisorsQuery();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showSecond, setShowSecond] = useState(!!client.advisor_name_2);

  // Reveal the second-advisor row whenever the server reports one — covers a
  // refetch landing after another tab set it.
  useEffect(() => {
    if (client.advisor_name_2) setShowSecond(true);
  }, [client.advisor_name_2]);

  const names = advisors.map(a => a.name);

  const save = async (patch) => {
    setError(null);
    setSaving(true);
    try {
      await updateClient({ id: client.public_id, ...patch }).unwrap();
    } catch (e) {
      setError(e?.data?.detail || 'Could not save the advisor.');
    } finally {
      setSaving(false);
    }
  };

  // Include any current value that isn't in the directory list (e.g. a legacy
  // free-text advisor entered before the picker existed) so it still displays
  // and isn't silently dropped when the operator opens the dropdown.
  const optionsFor = (current) => {
    const out = [...names];
    if (current && !out.includes(current)) out.unshift(current);
    return out;
  };

  const labelStyle = {
    color: work.muted, fontSize: '0.72rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
    // Fixed width so the primary and 2nd-advisor dropdowns share the same left
    // edge and line up vertically, despite the labels differing in length.
    display: 'inline-flex', alignItems: 'center', width: 96, flexShrink: 0,
  };
  const selectStyle = {
    border: `1px solid ${work.hairline}`, borderRadius: 4,
    padding: '0.3rem 0.5rem', fontSize: '0.85rem', color: work.ink,
    background: 'white', outline: 'none', maxWidth: 260,
  };
  const linkBtnStyle = {
    background: 'transparent', border: 0, padding: 0,
    color: work.tealBlue, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
  };

  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={labelStyle}>
          Advisor
        </span>
        <select
          value={client.advisor_name || ''}
          disabled={saving}
          onChange={(e) => save({ advisor_name: e.target.value })}
          style={selectStyle}
        >
          <option value="">— Select advisor —</option>
          {optionsFor(client.advisor_name).map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        {!showSecond && (
          <button type="button" onClick={() => setShowSecond(true)} style={linkBtnStyle}>
            <i className="bi bi-plus-lg me-1" />Add second advisor
          </button>
        )}
      </div>

      {showSecond && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={labelStyle}>
            2nd advisor
          </span>
          <select
            value={client.advisor_name_2 || ''}
            disabled={saving}
            onChange={(e) => save({ advisor_name_2: e.target.value })}
            style={selectStyle}
          >
            <option value="">— Select advisor —</option>
            {optionsFor(client.advisor_name_2).map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <button
            type="button"
            title="Remove second advisor"
            onClick={() => {
              setShowSecond(false);
              if (client.advisor_name_2) save({ advisor_name_2: '' });
            }}
            style={{ ...linkBtnStyle, color: work.muted }}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
      )}

      {advisors.length === 0 && (
        <span style={{ color: work.muted, fontSize: '0.72rem', fontStyle: 'italic' }}>
          No advisors configured yet — flag people as advisors in the company directory.
        </span>
      )}
      {error && <span style={{ color: work.red, fontSize: '0.72rem' }}>{error}</span>}
    </div>
  );
}
AdvisorPicker.propTypes = {
  client: PropTypes.object.isRequired,
  updateClient: PropTypes.func.isRequired,
};

// ─── Gemstone picker ─────────────────────────────────────────────────────────
// Same inline-edit feel as AdvisorPicker, right below it. Diamond-type tiers
// require a second advisor — the backend rejects the change if one isn't set,
// so surface that error rather than silently no-op-ing.
function GemstonePicker({ client, updateClient }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const current = gemstoneInfo(client.gemstone);

  const save = async (gemstone) => {
    setError(null);
    setSaving(true);
    try {
      await updateClient({ id: client.public_id, gemstone }).unwrap();
    } catch (e) {
      setError(e?.data?.detail || 'Could not save the gemstone.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      <span style={{
        color: work.muted, fontSize: '0.72rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', width: 96, flexShrink: 0,
      }}>
        Gemstone
      </span>
      {current && <i className="bi bi-suit-diamond-fill" style={{ color: current.color, fontSize: '0.9rem' }} />}
      <select
        value={client.gemstone || ''}
        disabled={saving}
        onChange={(e) => save(e.target.value)}
        style={{
          border: `1px solid ${work.hairline}`, borderRadius: 4,
          padding: '0.3rem 0.5rem', fontSize: '0.85rem', color: work.ink,
          background: 'white', outline: 'none', maxWidth: 260,
        }}
      >
        <option value="">— None —</option>
        {GEMSTONE_OPTIONS.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
      </select>
      {error && <span style={{ color: work.red, fontSize: '0.72rem' }}>{error}</span>}
    </div>
  );
}
GemstonePicker.propTypes = {
  client: PropTypes.object.isRequired,
  updateClient: PropTypes.func.isRequired,
};

// ─── Stage picker ────────────────────────────────────────────────────────────
// Which of the four "D" steps this client is in. Same inline-edit feel as
// AdvisorPicker/GemstonePicker, but worth showing the whole track rather
// than a dropdown: this is the one field on the page the client sees for
// themselves — it selects the lit step on their portal dashboard's progress
// bar — so the operator should be able to see where they are at a glance.
function StagePicker({ client, updateClient, onSaved }) {
  const [saving, setSaving] = useState(null); // the key being saved
  const [error, setError] = useState(null);
  const currentKey = client.workflow_status || DEFAULT_WORKFLOW_STAGE;
  const currentIdx = WORKFLOW_STAGE_OPTIONS.findIndex(s => s.key === currentKey);

  const save = async (key) => {
    if (key === currentKey) return;
    setError(null);
    setSaving(key);
    try {
      await updateClient({ id: client.public_id, workflow_status: key }).unwrap();
      // Tags are inert in this slice, so re-read rather than trusting
      // invalidation — otherwise the track stays on the old step.
      onSaved?.();
    } catch (e) {
      setError(e?.data?.detail || 'Could not save the stage.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      <span style={{
        color: work.muted, fontSize: '0.72rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', width: 96, flexShrink: 0,
      }}>
        Stage
      </span>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
        {WORKFLOW_STAGE_OPTIONS.map((s, i) => {
          const here = s.key === currentKey;
          const done = i < currentIdx;
          const busy = saving === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => save(s.key)}
              disabled={!!saving}
              title={`${s.label} - ${s.hint}`}
              aria-pressed={here}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                border: `1px solid ${here ? s.color : work.hairline}`,
                background: here ? s.color : done ? 'rgba(10,41,71,0.05)' : 'white',
                color: here ? 'white' : done ? work.ink : work.muted,
                borderRadius: 999, padding: '0.2rem 0.6rem',
                fontSize: '0.78rem', fontWeight: here ? 800 : 600,
                cursor: saving ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {done && <i className="bi bi-check-lg" style={{ fontSize: '0.7rem' }} />}
              {s.label}
            </button>
          );
        })}
      </div>
      <span style={{ color: work.muted, fontSize: '0.72rem' }}>
        Shown as the progress bar on the client&apos;s portal dashboard.
      </span>
      {error && <span style={{ color: work.red, fontSize: '0.72rem' }}>{error}</span>}
    </div>
  );
}
StagePicker.propTypes = {
  client: PropTypes.object.isRequired,
  updateClient: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

// ─── AUM client (FSC household link) ─────────────────────────────────────────
// Ties this portal household to its Salesforce FSC household, which is what
// makes "the person in the portal" and "the client with the money" one client.
//
// Nothing about the money is stored here. FSC carries the Orion household id,
// so the figures below are read straight off the nightly Orion cache whenever
// the page loads — there is one number, in Orion, and this is a view of it.
// `aum_as_of` is always shown for that reason: a stale figure presented as
// live is the failure worth engineering against.
function AumClientCard({ client, isMobile }) {
  const { data, isLoading, refetch } = useGetClientFscLinkQuery({ id: client.public_id });
  const [setLink] = useSetClientFscLinkMutation();
  const [clearLink] = useClearClientFscLinkMutation();
  const [searching, setSearching] = useState(false);
  const [busyForId, setBusyForId] = useState(null);
  const [error, setError] = useState(null);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  const link = data?.link;
  const facts = data?.facts;
  const linked = !!data?.linked;
  const visible = data?.visible !== false;

  const choose = async (household) => {
    setError(null);
    setBusyForId(household.fsc_id);
    try {
      await setLink({ id: client.public_id, fsc_household_id: household.fsc_id }).unwrap();
      setSearching(false);
      refetch();
    } catch (e) {
      const detail = e?.data?.detail;
      setError(typeof detail === 'object' ? detail.message : (detail || 'Could not link that household.'));
    } finally {
      setBusyForId(null);
    }
  };

  const unlink = async () => {
    setError(null);
    try {
      await clearLink({ id: client.public_id }).unwrap();
      setConfirmUnlink(false);
      refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not unlink.');
    }
  };

  const card = (children) => (
    <div style={{
      background: work.surface, border: `1px solid ${work.hairline}`,
      borderTop: `3px solid ${work.tealBlue}`, borderRadius: 6,
      padding: '1rem 1.25rem', marginBottom: '1rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.15rem' }}>
        <i className="bi bi-graph-up-arrow" style={{ color: work.tealBlue }} />
        <span style={{ fontWeight: 800, color: work.deepBlue, fontSize: '1rem' }}>Salesforce client</span>
        {linked && visible && (
          <span style={{
            background: 'rgba(26,127,78,0.12)', color: work.green, borderRadius: 4,
            padding: '0.1rem 0.4rem', fontSize: '0.65rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            <i className="bi bi-link-45deg" /> Linked
          </span>
        )}
      </div>
      {children}
      {error && (
        <div style={{
          background: 'rgba(176,0,32,0.06)', border: `1px solid ${work.red}`, color: work.red,
          borderRadius: 4, padding: '0.45rem 0.7rem', fontSize: '0.8rem', marginTop: '0.7rem',
        }}>
          {error}
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return card(<p style={{ color: work.muted, fontSize: '0.85rem', margin: 0 }}>Loading…</p>);
  }

  // Linked to a household at an office this user can't see. Said plainly
  // rather than shown as unlinked, so nobody "fixes" it by linking elsewhere.
  if (linked && !visible) {
    return card(
      <p style={{ color: work.muted, fontSize: '0.85rem', margin: '0.15rem 0 0' }}>
        This household is linked to an FSC client at another office, which isn&apos;t visible to you.
        Ask someone with access to that office if it needs changing.
      </p>
    );
  }

  if (!linked) {
    return card(
      <>
        <p style={{ color: work.muted, fontSize: '0.85rem', margin: '0.15rem 0 0.8rem' }}>
          Not linked to a Salesforce client yet. Linking ties this portal household to its FSC
          household, which brings across the AUM, the client-since date and the document history -
          and puts the Portal badge on them in the client book.
        </p>
        {searching ? (
          <FscHouseholdPicker
            autoFocus
            busyForId={busyForId}
            onSelect={choose}
            onCancel={() => { setSearching(false); setError(null); }}
          />
        ) : (
          <button type="button" onClick={() => setSearching(true)} style={{
            background: work.tealBlue, color: 'white', border: 0, borderRadius: 4,
            padding: '0.45rem 0.9rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
          }}>
            <i className="bi bi-search me-1" />Link to Salesforce client
          </button>
        )}
      </>
    );
  }

  // Linked and visible. The household may still be missing from the cache —
  // see get_portal_link's LEFT JOIN — so don't assume a name is present.
  const stale = !link?.household_name;
  const aum = formatUsd(facts?.current_aum);
  const since = formatDate(facts?.client_start_date);
  const asOf = formatDate(facts?.aum_as_of);

  const tile = (label, value, sub) => (
    <div style={{
      background: work.canvas, border: `1px solid ${work.hairline}`, borderRadius: 4,
      padding: '0.6rem 0.75rem', minWidth: 0,
    }}>
      <div style={{
        color: work.muted, fontSize: '0.66rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.5px',
      }}>{label}</div>
      <div style={{ color: work.ink, fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.2, marginTop: 2 }}>
        {value ?? '—'}
      </div>
      {sub && <div style={{ color: work.muted, fontSize: '0.68rem', marginTop: 1 }}>{sub}</div>}
    </div>
  );

  return card(
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.7rem' }}>
        <Link
          to={`/work/clients/fsc/${link.fsc_household_id}`}
          style={{ color: work.tealBlue, fontWeight: 700, fontSize: '0.95rem', textDecoration: 'none' }}
        >
          {link.household_name || link.fsc_household_id}
        </Link>
        {link.rep_name && <span style={{ color: work.muted, fontSize: '0.8rem' }}>· {link.rep_name}</span>}
        {link.rep_firm_name && <span style={{ color: work.muted, fontSize: '0.8rem' }}>· {link.rep_firm_name}</span>}
      </div>

      {stale ? (
        <p style={{ color: work.goldenrod, fontSize: '0.8rem', margin: '0 0 0.7rem' }}>
          <i className="bi bi-exclamation-triangle me-1" />
          Linked to {link.fsc_household_id}, which isn&apos;t in the household cache in this
          environment - the figures below will fill in once the FSC sync has run here.
        </p>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, minmax(0, 1fr))',
          gap: '0.5rem', marginBottom: '0.7rem',
        }}>
          {/* No net flow tile: net new AUM is still being worked on, so the
              only place that figure is reported is Work → Net New AUM, where
              it carries its own work-in-progress warning. */}
          {tile('AUM', aum, asOf ? `as of ${asOf}` : 'not in Orion')}
          {tile('Client since', since)}
          {tile('Documents', facts?.doc_count ?? 0, 'in the FSC book')}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {searching ? (
          <div style={{ flex: '1 1 100%' }}>
            <FscHouseholdPicker
              autoFocus
              busyForId={busyForId}
              onSelect={choose}
              onCancel={() => { setSearching(false); setError(null); }}
            />
          </div>
        ) : confirmUnlink ? (
          <>
            <span style={{ color: work.ink, fontSize: '0.82rem' }}>
              Unlink? The FSC client is untouched - only the tie is removed.
            </span>
            <button type="button" onClick={unlink} style={{
              background: work.red, color: 'white', border: 0, borderRadius: 4,
              padding: '0.35rem 0.8rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
            }}>
              Unlink
            </button>
            <button type="button" onClick={() => setConfirmUnlink(false)} style={{
              background: 'transparent', color: work.deepBlue,
              border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
              padding: '0.35rem 0.8rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
            }}>
              Keep
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setSearching(true)} style={{
              background: 'transparent', color: work.deepBlue,
              border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
              padding: '0.35rem 0.8rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
            }}>
              <i className="bi bi-arrow-repeat me-1" />Change
            </button>
            <button type="button" onClick={() => setConfirmUnlink(true)} style={{
              background: 'transparent', color: work.muted, border: 0, padding: '0.35rem 0',
              fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline',
            }}>
              Unlink
            </button>
            {link.reviewed_by && (
              <span style={{ color: work.muted, fontSize: '0.72rem', marginLeft: 'auto' }}>
                Linked {formatDate(link.reviewed_at) || ''} · {link.match_method}
              </span>
            )}
          </>
        )}
      </div>
    </>
  );
}
AumClientCard.propTypes = {
  client: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
};

// ─── "Your documents are waiting" reminder ───────────────────────────────────
// Emails the client the list of requests they still owe us, with a link to the
// portal login. Two moments it earns its keep: a client who has gone quiet on
// their original list, and - the case this was built for - an advisor ADDING a
// request to a list the client already believes they finished, which otherwise
// happens entirely silently.
//
// Confirm-before-send, showing the actual list. This is the only button on the
// page that puts something in a real client's inbox, so the advisor sees
// exactly what the client will see before it goes.
// Close a popover on Escape or a click anywhere outside it. Both "Email the
// client" buttons use it: each one hangs a panel over the page, and a panel
// you can only shut by finding its own Cancel button reads as the page being
// stuck. Returns a ref to put on the popover's outermost wrapper.
function useDismissOnOutside(open, onClose) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    // `mousedown`, not `click`: closing on the press means the panel is gone
    // before whatever was clicked underneath reacts.
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  return ref;
}

function RemindClientButton({ client, dirty }) {
  const { data, refetch, isFetching } = useGetClientDocumentsReminderQuery({ id: client.public_id });
  const [send, { isLoading: isSending }] = useSendClientDocumentsReminderMutation();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [sentAt, setSentAt] = useState(null);
  const wrapRef = useDismissOnOutside(open, useCallback(() => setOpen(false), []));

  const canSend = !!data?.can_send;
  const items = data?.items || [];
  const lastSent = sentAt || data?.last_sent_at;

  const doSend = async () => {
    setError(null);
    try {
      const res = await send({ id: client.public_id }).unwrap();
      setSentAt(res.last_sent_at || new Date().toISOString());
      setOpen(false);
      refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not send the email.');
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => { setError(null); setOpen(o => !o); }}
        disabled={!canSend || isFetching}
        title={canSend ? 'Email this client the list of documents still outstanding' : (data?.blocked_reason || '')}
        style={{
          background: 'transparent',
          color: canSend ? work.deepBlue : work.muted,
          border: `1px solid ${canSend ? work.hairlineStrong : work.hairline}`,
          borderRadius: 4, padding: '0.45rem 0.9rem',
          fontWeight: 700, fontSize: '0.82rem',
          cursor: canSend ? 'pointer' : 'not-allowed',
          whiteSpace: 'nowrap',
        }}
      >
        <i className="bi bi-envelope me-1" />
        Email the client
        {canSend && items.length > 0 && (
          <span style={{ color: work.muted, fontWeight: 600 }}> ({items.length})</span>
        )}
      </button>

      {lastSent && (
        <div style={{ color: work.muted, fontSize: '0.7rem', marginTop: 2, textAlign: 'right' }}>
          Last sent {formatDate(lastSent) || 'recently'}
        </div>
      )}

      {open && (
        <div style={{
          position: 'absolute', zIndex: 20, right: 0, top: '100%', marginTop: 6, width: 340,
          background: 'white', border: `1px solid ${work.hairlineStrong}`, borderRadius: 6,
          boxShadow: '0 8px 20px rgba(10,41,71,0.15)', padding: '0.85rem 1rem',
        }}>
          <div style={{ fontWeight: 800, color: work.ink, fontSize: '0.9rem', marginBottom: '0.3rem' }}>
            Email {client.first_name || 'this client'}?
          </div>
          <p style={{ color: work.muted, fontSize: '0.78rem', margin: '0 0 0.5rem' }}>
            Goes to <strong style={{ color: work.ink }}>{data?.email}</strong> with a link to the
            portal login and {items.length === 1 ? 'this item' : `these ${items.length} items`}:
          </p>
          <ul style={{
            margin: '0 0 0.6rem', paddingLeft: '1.1rem', color: work.ink, fontSize: '0.8rem',
            maxHeight: 150, overflowY: 'auto',
          }}>
            {items.map(i => <li key={i}>{i}</li>)}
          </ul>
          {/* The email doesn't ask for a reply, but clients send them anyway,
              so say which colleague that lands on. Point people first - they
              are already who gets told when this client finishes uploading. */}
          <p style={{ color: work.muted, fontSize: '0.75rem', margin: '0 0 0.6rem' }}>
            <i className="bi bi-reply me-1" />
            {(data?.reply_to || []).length > 0 ? (
              <>
                Replies go to{' '}
                <strong style={{ color: work.ink }}>
                  {(data.reply_to_names || data.reply_to).join(', ')}
                </strong>
                {data.reply_to_source === 'advisor' && ' (no point people set, so the advisor)'}.
              </>
            ) : (
              <>No point people or advisor on file, so replies go to the sending mailbox.</>
            )}
          </p>
          {/* The cc is on the message the CLIENT reads, so they can see who
              was copied - say so here rather than letting the advisor find
              out from a reply-all. Point people only, never the advisor
              fallback above. */}
          {(data?.cc || []).length > 0 && (
            <p style={{ color: work.muted, fontSize: '0.75rem', margin: '0 0 0.6rem' }}>
              <i className="bi bi-people me-1" />
              Copied (cc, visible to the client):{' '}
              <strong style={{ color: work.ink }}>
                {(data.cc_names || data.cc).join(', ')}
              </strong>
            </p>
          )}
          {/* Unsaved checklist edits are not in the email yet - the list above
              comes from the server, which hasn't seen them. */}
          {dirty && (
            <p style={{ color: work.goldenrod, fontSize: '0.75rem', margin: '0 0 0.6rem' }}>
              <i className="bi bi-exclamation-triangle me-1" />
              You have unsaved checklist changes. Save them first if they should be in this email.
            </p>
          )}
          {error && (
            <p style={{ color: work.red, fontSize: '0.78rem', margin: '0 0 0.6rem' }}>{error}</p>
          )}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={doSend} disabled={isSending} style={{
              background: work.tealBlue, color: 'white', border: 0, borderRadius: 4,
              padding: '0.4rem 0.85rem', fontWeight: 700, fontSize: '0.8rem',
              cursor: isSending ? 'wait' : 'pointer',
            }}>
              {isSending ? 'Sending…' : 'Send email'}
            </button>
            <button type="button" onClick={() => setOpen(false)} style={{
              background: 'transparent', color: work.deepBlue,
              border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
              padding: '0.4rem 0.85rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
            }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
RemindClientButton.propTypes = {
  client: PropTypes.object.isRequired,
  // Whether the checklist has unsaved edits, so the confirm panel can warn
  // that they won't be in the email.
  dirty: PropTypes.bool,
};

// ─── Documents we share OUT to the client ────────────────────────────────────
// The mirror of ReceivedFiles below: that is what they sent us, this is what
// we sent them. Kept as its own panel rather than a direction column inside
// one list, because the two answer different questions - "has this client
// given us what we asked for" vs "has this client seen what we sent" - and an
// advisor is only ever asking one of them at a time.
//
// The read receipt is the point of the list. "Shared 3 days ago, not opened"
// is the thing worth knowing before a review meeting, so it is on every row
// rather than behind a click.

function formatFileSize(n) {
  if (!n && n !== 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Keep in step with MAX_DOC_BYTES / MAX_VIDEO_BYTES in routers/clients.py.
// Videos are "2 GB" in decimal because the size column is a signed INT.
const MAX_SHARE_DOC_BYTES = 25 * 1024 * 1024;
const MAX_SHARE_VIDEO_BYTES = 2000000000;
// Videos go up in slices to dodge nginx's 26 MB request cap; S3 needs every
// slice but the last to be at least 5 MB. Three in flight at once is enough to
// fill an office uplink without starving the rest of the page.
const VIDEO_PART_BYTES = 16 * 1024 * 1024;
const VIDEO_PARALLEL_PARTS = 3;

// Caught here rather than after a 2 GB upload fails at the server.
const shareSizeProblem = (file) => (isVideoFile(file.name)
  ? (file.size > MAX_SHARE_VIDEO_BYTES ? 'Videos can be up to 2 GB.' : null)
  : (file.size > MAX_SHARE_DOC_BYTES ? 'Documents can be up to 25 MB. Videos (MP4, MOV, WebM) up to 2 GB.' : null));

// One file about to be shared, with the labelling that belongs to it alone.
// The filename stays on top as the anchor — it is what the advisor recognises
// the row by — and the title beneath is what the CLIENT will see instead.
function ShareFileCard({ item, index, catalog, onPatch, onRemove }) {
  const inputStyle = {
    padding: '0.4rem 0.5rem', fontSize: '0.82rem', color: work.ink,
    border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
  };
  const video = isVideoFile(item.file.name);
  const sizeProblem = shareSizeProblem(item.file);
  return (
    <div style={{
      background: work.surface, border: `1px solid ${work.hairline}`,
      // Only the things that can block the share are marked on the card: a
      // missing type, or a file over its size limit.
      borderLeft: `3px solid ${sizeProblem ? work.red : item.typeKey ? work.tealBlue : work.goldenrod}`,
      borderRadius: 4, padding: '0.6rem 0.75rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.45rem' }}>
        <i className={`bi ${video ? 'bi-camera-video' : 'bi-file-earmark'}`} style={{ color: work.muted }} />
        <span style={{
          flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', fontSize: '0.82rem', fontWeight: 600, color: work.ink,
        }}>
          {item.file.name}
        </span>
        <span style={{ color: work.muted, fontSize: '0.72rem' }}>{formatFileSize(item.file.size)}</span>
        <button
          type="button" aria-label={`Remove ${item.file.name}`} onClick={onRemove}
          style={{ background: 'transparent', border: 0, color: work.muted, cursor: 'pointer', lineHeight: 1 }}
        >
          <i className="bi bi-x-lg" style={{ fontSize: '0.7rem' }} />
        </button>
      </div>
      {sizeProblem && (
        <div style={{ color: work.red, fontSize: '0.76rem', marginBottom: '0.4rem' }}>
          <i className="bi bi-exclamation-circle me-1" />{sizeProblem}
        </div>
      )}
      {item.progress != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.45rem' }}>
          <div
            role="progressbar" aria-label={`Uploading ${item.file.name}`}
            aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(item.progress * 100)}
            style={{ flex: 1, height: 6, background: work.hairline, borderRadius: 3, overflow: 'hidden' }}
          >
            <div style={{
              width: `${Math.round(item.progress * 100)}%`, height: '100%',
              background: work.tealBlue, transition: 'width 0.3s',
            }} />
          </div>
          <span style={{ color: work.muted, fontSize: '0.72rem', minWidth: 70, textAlign: 'right' }}>
            {item.progress >= 1 ? 'Finishing…' : `Uploading ${Math.round(item.progress * 100)}%`}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
        <select
          value={item.typeKey}
          onChange={(e) => onPatch({ typeKey: e.target.value })}
          aria-label={`What is ${item.file.name}?`}
          style={{ ...inputStyle, flex: '1 1 190px' }}
        >
          <option value="">What is this? (required)</option>
          {(catalog || []).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <input
          type="text"
          value={item.title}
          onChange={(e) => onPatch({ title: e.target.value })}
          aria-label={`Title for ${item.file.name}`}
          placeholder={index === 0 ? 'Title the client sees (optional)' : 'Title (optional)'}
          style={{ ...inputStyle, flex: '1 1 200px' }}
        />
        <input
          type="number"
          value={item.year}
          onChange={(e) => onPatch({ year: e.target.value })}
          aria-label={`Year for ${item.file.name}`}
          placeholder="Year" min="1900" max="2100"
          title="Which year this document is ABOUT, not today's date"
          style={{ ...inputStyle, width: 85 }}
        />
      </div>

      <textarea
        value={item.note}
        onChange={(e) => onPatch({ note: e.target.value })}
        aria-label={`Note for ${item.file.name}`}
        placeholder="A note about this document (optional) - shown in the portal beside it."
        rows={1}
        style={{ ...inputStyle, width: '100%', marginTop: '0.4rem', resize: 'vertical', fontFamily: 'inherit' }}
      />
    </div>
  );
}
ShareFileCard.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  catalog: PropTypes.array,
  onPatch: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

function ShareComposer({ client, members, catalog, onShared, onError }) {
  const [share] = useShareClientDocumentMutation();
  const [startVideo] = useStartSharedVideoMutation();
  const [uploadVideoPart] = useUploadSharedVideoPartMutation();
  const [completeVideo] = useCompleteSharedVideoMutation();
  const [abortVideo] = useAbortSharedVideoMutation();
  const [notify] = useSendClientSharedNotificationMutation();
  // Covers the whole batch, videos included — a single mutation's isLoading
  // would go false between a video's slices.
  const [isSharing, setIsSharing] = useState(false);
  // Closing the composer mid-upload (the panel's Cancel) must stop a video
  // rather than let it finish and share itself in the background.
  const cancelled = useRef(false);
  useEffect(() => () => { cancelled.current = true; }, []);
  const { data: notifyPreview } = useGetClientSharedNotifyPreviewQuery({ id: client.public_id });

  // One entry per file, each owning its own labelling:
  //   { uid, file, typeKey, title, year, note }
  // Batch-level metadata was wrong for the common case — a plan, a fee
  // schedule and a statement shared together are three different documents
  // with three different names, and one type dropdown across all of them
  // filed two of the three incorrectly. Audience stays batch-level on
  // purpose: it answers "who is this delivery for", and a file that needs a
  // different audience is a different delivery.
  const [items, setItems] = useState([]);
  const [audience, setAudience] = useState('household');
  const [picked, setPicked] = useState([]);   // client_user ids, audience==='members'
  // OFF by default. Sharing a file and telling the client about it are two
  // decisions, and the second one reaches a real person's inbox - it should
  // be something you opt into, not something you have to notice and undo.
  // Anything shared without an email is flagged "not emailed" in the list and
  // counted on the Email the client button, so it cannot be silently lost.
  const [shouldNotify, setShouldNotify] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef(null);

  const canNotify = !!notifyPreview?.can_send;
  // Every file needs a type — it is the one field that decides where the
  // document is filed and what the client sees it called when no title is
  // typed, so it can't fall back to anything sensible.
  const untyped = items.filter(it => !it.typeKey).length;
  const oversized = items.filter(it => shareSizeProblem(it.file)).length;
  const ready = items.length > 0 && untyped === 0 && oversized === 0
    && (audience === 'household' || picked.length > 0);

  const reset = () => {
    setItems([]);
    setAudience('household'); setPicked([]);
    if (fileInput.current) fileInput.current.value = '';
  };

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    setItems(prev => {
      // A new row inherits the type of the last row that has one. Sharing
      // three parts of the same review packet shouldn't mean picking
      // "Review meeting packet" three times — but it is a prefill on a
      // per-file field, not a batch setting, so any row can differ.
      const lastType = [...prev].reverse().find(it => it.typeKey)?.typeKey || '';
      return [
        ...prev,
        ...incoming.map(file => ({
          uid: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          // A video is almost always a video message; inheriting "Financial
          // plan" from the PDF above it would be the wrong guess.
          typeKey: isVideoFile(file.name) ? 'shared_video' : lastType,
          title: '',
          year: '',
          note: '',
          progress: null,
        })),
      ];
    });
  };

  const patchItem = (uid, patch) =>
    setItems(prev => prev.map(it => (it.uid === uid ? { ...it, ...patch } : it)));
  const removeItem = (uid) => setItems(prev => prev.filter(it => it.uid !== uid));

  // start → slices (a few in flight, each retried) → complete. Any failure,
  // or the composer closing, aborts the S3 upload so no parts are left behind.
  const shareVideo = async (it, labels) => {
    const payload = { ...labels, filename: it.file.name, size_bytes: it.file.size };
    const started = await startVideo({ id: client.public_id, share: payload }).unwrap();
    const ref = { id: client.public_id, s3_key: started.s3_key, upload_id: started.upload_id };
    const total = Math.ceil(it.file.size / VIDEO_PART_BYTES);
    let next = 1;
    let done = 0;
    let failed = false;
    patchItem(it.uid, { progress: 0 });

    const worker = async () => {
      while (next <= total && !failed) {
        if (cancelled.current) throw new Error('Upload cancelled.');
        const n = next++;
        const chunk = it.file.slice((n - 1) * VIDEO_PART_BYTES, Math.min(n * VIDEO_PART_BYTES, it.file.size));
        for (let attempt = 1; ; attempt += 1) {
          try {
            await uploadVideoPart({ ...ref, part_number: n, chunk }).unwrap();
            break;
          } catch (e) {
            // A 400 is a verdict (not a video, upload gone), not a blip.
            if (attempt >= 3 || e?.status === 400) throw e;
            await new Promise(r => setTimeout(r, 1500 * attempt));
          }
        }
        done += 1;
        patchItem(it.uid, { progress: done / total });
      }
    };

    try {
      await Promise.all(Array.from({ length: Math.min(VIDEO_PARALLEL_PARTS, total) }, () => worker().catch((e) => {
        failed = true;
        throw e;
      })));
      if (cancelled.current) throw new Error('Upload cancelled.');
      return await completeVideo({ ...ref, share: payload }).unwrap();
    } catch (e) {
      abortVideo(ref).catch(() => {});
      patchItem(it.uid, { progress: null });
      throw e;
    }
  };

  const doShare = async () => {
    onError(null);
    setIsSharing(true);
    // Files go up one at a time on purpose: a failure on the third must leave
    // the first two shared, not roll the batch back. Anything that did land
    // is still announced.
    const sharedIds = [];
    let failure = null;
    for (const it of items) {
      const labels = {
        type_key: it.typeKey,
        title: it.title,
        document_year: it.year ? Number(it.year) : undefined,
        note: it.note,
        audience,
        recipient_ids: audience === 'members' ? picked : [],
      };
      try {
        const row = isVideoFile(it.file.name)
          ? await shareVideo(it, labels)
          : await share({ id: client.public_id, file: it.file, ...labels }).unwrap();
        if (row?.id) sharedIds.push(row.id);
      } catch (e) {
        failure = `${it.file.name}: ${e?.data?.detail || e?.message || 'could not be shared.'}`;
        break;
      }
    }
    setIsSharing(false);
    if (cancelled.current) return;

    if (sharedIds.length && shouldNotify && canNotify) {
      try {
        // One email for the whole batch - three files shared together are one
        // thing as far as the client is concerned.
        await notify({ id: client.public_id, ids: sharedIds }).unwrap();
      } catch (e) {
        failure = failure || `Shared, but the email didn't send: ${e?.data?.detail || 'unknown error'}`;
      }
    }

    if (failure) onError(failure);
    if (sharedIds.length) { reset(); onShared(); }
  };

  return (
    <div style={{ padding: '0.85rem 1rem', borderBottom: `1px solid ${work.hairline}`, background: work.canvas }}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer?.files); }}
        onClick={() => fileInput.current?.click()}
        style={{
          border: `2px dashed ${dragging ? work.tealBlue : work.hairlineStrong}`,
          background: dragging ? 'rgba(0,105,166,0.05)' : work.surface,
          borderRadius: 6, padding: '1rem', textAlign: 'center', cursor: 'pointer',
          color: work.muted, fontSize: '0.82rem',
        }}
      >
        <i className="bi bi-cloud-arrow-up" style={{ fontSize: '1.3rem', color: work.tealBlue, display: 'block', marginBottom: 4 }} />
        Drop files here, or click to choose. Documents up to 25 MB; videos (MP4, MOV, WebM) up to 2 GB.
        <input
          ref={fileInput} type="file" multiple hidden
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>

      {/* One card per file — its own type, title, year and note, the same way
          each custom request on the requested side is its own row. */}
      {items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.7rem' }}>
          {items.map((it, i) => (
            <ShareFileCard
              key={it.uid}
              item={it}
              index={i}
              catalog={catalog}
              onPatch={(patch) => patchItem(it.uid, patch)}
              onRemove={() => removeItem(it.uid)}
            />
          ))}
        </div>
      )}

      {/* Audience only matters when there's more than one person it could
          reach. A household of one has no choice to make. */}
      {members.length >= 2 && (
        <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: work.ink }}>
          <label style={{ marginRight: '1rem', cursor: 'pointer' }}>
            <input
              type="radio" checked={audience === 'household'}
              onChange={() => setAudience('household')} style={{ marginRight: 4 }}
            />
            Everyone in the household
          </label>
          <label style={{ cursor: 'pointer' }}>
            <input
              type="radio" checked={audience === 'members'}
              onChange={() => setAudience('members')} style={{ marginRight: 4 }}
            />
            Only certain people
          </label>
          {audience === 'members' && (
            <div style={{ marginTop: '0.35rem', paddingLeft: '1.2rem' }}>
              {members.map(m => (
                <label key={m.id} style={{ display: 'block', cursor: 'pointer', color: work.ink }}>
                  <input
                    type="checkbox"
                    checked={picked.includes(m.id)}
                    onChange={(e) => setPicked(prev => (
                      e.target.checked ? [...prev, m.id] : prev.filter(x => x !== m.id)
                    ))}
                    style={{ marginRight: 4 }}
                  />
                  {fullName(m.first_name, m.last_name, m.email)}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.7rem' }}>
        <button
          type="button" onClick={doShare} disabled={!ready || isSharing}
          // Says WHICH file still needs attention rather than just going grey,
          // since with several cards on screen "not ready" isn't self-evident.
          title={untyped > 0 ? `${untyped} file${untyped === 1 ? '' : 's'} still need a type`
            : oversized > 0 ? 'Remove the file that is over its size limit' : ''}
          style={{
            background: ready ? work.tealBlue : work.hairline,
            color: ready ? 'white' : work.muted,
            border: 0, borderRadius: 4, padding: '0.45rem 1rem',
            fontWeight: 700, fontSize: '0.82rem', cursor: ready && !isSharing ? 'pointer' : 'default',
          }}
        >
          {isSharing ? 'Sharing…' : `Share ${items.length || ''} ${items.length === 1 ? 'file' : 'files'}`.trim()}
        </button>
        {untyped > 0 && (
          <span style={{ color: work.goldenrod, fontSize: '0.78rem' }}>
            <i className="bi bi-exclamation-triangle me-1" />
            {untyped} {untyped === 1 ? 'file needs' : 'files need'} a type.
          </span>
        )}
        <label style={{
          fontSize: '0.8rem', color: canNotify ? work.ink : work.muted,
          cursor: canNotify ? 'pointer' : 'not-allowed',
        }} title={canNotify ? '' : (notifyPreview?.blocked_reason || '')}>
          <input
            type="checkbox" checked={shouldNotify && canNotify} disabled={!canNotify}
            onChange={(e) => setShouldNotify(e.target.checked)} style={{ marginRight: 4 }}
          />
          Email {client.first_name || 'the client'} now
        </label>
        <span style={{ color: work.muted, fontSize: '0.75rem' }}>
          Or share first and email later - use <strong>Email the client</strong> above.
        </span>
      </div>
      {!canNotify && notifyPreview?.blocked_reason && (
        <div style={{ color: work.muted, fontSize: '0.75rem', marginTop: 4 }}>
          <i className="bi bi-info-circle me-1" />
          {notifyPreview.blocked_reason} You can still share the file - they just won&apos;t be emailed.
        </div>
      )}
      {/* The bucket is WORM. Say so before the click, not after. */}
      <div style={{ color: work.muted, fontSize: '0.72rem', marginTop: '0.5rem' }}>
        <i className="bi bi-shield-lock me-1" />
        Shared files are retained for 7 years and cannot be deleted. Un-sharing takes
        a file away from the client but does not remove it - check the household before you share.
      </div>
    </div>
  );
}
ShareComposer.propTypes = {
  client: PropTypes.object.isRequired,
  members: PropTypes.array.isRequired,
  catalog: PropTypes.array,
  onShared: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

// "Email the client" for the SHARED side — the mirror of RemindClientButton,
// and deliberately the same popover: same confirm-before-it-reaches-a-real-
// person shape, same reply-to disclosure, same "Last sent" line.
//
// WHAT IT SENDS is the shared-side equivalent of "still outstanding": every
// document NOBODY in the household has opened yet. That covers both jobs with
// one button — "I just shared this, tell them" (a new file is unopened by
// definition) and "they still haven't looked" (re-sendable, unlike a list of
// never-announced files, which empties permanently after one send). A document
// someone has already opened needs no email.
function NotifySharedButton({ client, documents, onSent }) {
  const { data, refetch: refetchPreview, isFetching } = useGetClientSharedNotifyPreviewQuery({ id: client.public_id });
  const [send, { isLoading: isSending }] = useSendClientSharedNotificationMutation();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const wrapRef = useDismissOnOutside(open, useCallback(() => setOpen(false), []));

  const unopened = documents.filter(d => !(d.recipients || []).some(r => r.first_viewed_at));
  // Most recent announcement across everything shared, so the advisor can see
  // at a glance whether this household has been chased lately.
  const lastSent = documents
    .map(d => d.notified_at).filter(Boolean)
    .sort().slice(-1)[0] || null;

  const blocked = data?.can_send === false
    ? data.blocked_reason
    : unopened.length === 0
      ? (documents.length === 0
        ? 'Nothing has been shared with this client yet.'
        : 'Everything shared has been opened - there is nothing to tell them about.')
      : null;
  const canSend = !blocked;

  const doSend = async () => {
    setError(null);
    try {
      await send({ id: client.public_id, ids: unopened.map(d => d.id) }).unwrap();
      setOpen(false);
      refetchPreview();
      onSent();
    } catch (e) {
      setError(e?.data?.detail || 'Could not send the email.');
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => { setError(null); setOpen(o => !o); }}
        disabled={!canSend || isFetching}
        title={canSend ? 'Email this client about the documents they have not opened' : blocked}
        style={{
          background: 'transparent',
          color: canSend ? work.deepBlue : work.muted,
          border: `1px solid ${canSend ? work.hairlineStrong : work.hairline}`,
          borderRadius: 4, padding: '0.45rem 0.9rem',
          fontWeight: 700, fontSize: '0.82rem',
          cursor: canSend ? 'pointer' : 'not-allowed',
          whiteSpace: 'nowrap',
        }}
      >
        <i className="bi bi-envelope me-1" />
        Email the client
        {unopened.length > 0 && (
          <span style={{ color: work.muted, fontWeight: 600 }}> ({unopened.length})</span>
        )}
      </button>

      {lastSent && (
        <div style={{ color: work.muted, fontSize: '0.7rem', marginTop: 2, textAlign: 'right' }}>
          Last sent {formatDate(lastSent) || 'recently'}
        </div>
      )}

      {open && (
        <div style={{
          position: 'absolute', zIndex: 20, right: 0, top: '100%', marginTop: 6, width: 340,
          background: 'white', border: `1px solid ${work.hairlineStrong}`, borderRadius: 6,
          boxShadow: '0 8px 20px rgba(10,41,71,0.15)', padding: '0.85rem 1rem',
        }}>
          <div style={{ fontWeight: 800, color: work.ink, fontSize: '0.9rem', marginBottom: '0.3rem' }}>
            Email {client.first_name || 'this client'}?
          </div>
          <p style={{ color: work.muted, fontSize: '0.78rem', margin: '0 0 0.5rem' }}>
            Goes to <strong style={{ color: work.ink }}>{data?.email}</strong> with a link to the
            portal login and {unopened.length === 1 ? 'this document' : `these ${unopened.length} documents`}:
          </p>
          <ul style={{
            margin: '0 0 0.6rem', paddingLeft: '1.1rem', color: work.ink, fontSize: '0.8rem',
            maxHeight: 150, overflowY: 'auto',
          }}>
            {unopened.map(d => (
              <li key={d.id}>
                {d.display_title}
                {d.notified_at && (
                  <span style={{ color: work.muted }}> — already emailed {formatDate(d.notified_at)}</span>
                )}
              </li>
            ))}
          </ul>
          <p style={{ color: work.muted, fontSize: '0.75rem', margin: '0 0 0.6rem' }}>
            <i className="bi bi-reply me-1" />
            {(data?.reply_to || []).length > 0 ? (
              <>
                Replies go to{' '}
                <strong style={{ color: work.ink }}>
                  {(data.reply_to_names || data.reply_to).join(', ')}
                </strong>
                {data.reply_to_source === 'advisor' && ' (no point people set, so the advisor)'}.
              </>
            ) : (
              <>No point people or advisor on file, so replies go to the sending mailbox.</>
            )}
          </p>
          {/* The cc is on the message the CLIENT reads, so they can see who
              was copied - say so here rather than letting the advisor find
              out from a reply-all. Point people only, never the advisor
              fallback above. */}
          {(data?.cc || []).length > 0 && (
            <p style={{ color: work.muted, fontSize: '0.75rem', margin: '0 0 0.6rem' }}>
              <i className="bi bi-people me-1" />
              Copied (cc, visible to the client):{' '}
              <strong style={{ color: work.ink }}>
                {(data.cc_names || data.cc).join(', ')}
              </strong>
            </p>
          )}
          {error && (
            <p style={{ color: work.red, fontSize: '0.78rem', margin: '0 0 0.6rem' }}>{error}</p>
          )}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={doSend} disabled={isSending} style={{
              background: work.tealBlue, color: 'white', border: 0, borderRadius: 4,
              padding: '0.4rem 0.85rem', fontWeight: 700, fontSize: '0.8rem',
              cursor: isSending ? 'wait' : 'pointer',
            }}>
              {isSending ? 'Sending…' : 'Send email'}
            </button>
            <button type="button" onClick={() => setOpen(false)} style={{
              background: 'transparent', color: work.deepBlue,
              border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
              padding: '0.4rem 0.85rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
            }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
NotifySharedButton.propTypes = {
  client: PropTypes.object.isRequired,
  documents: PropTypes.array.isRequired,
  onSent: PropTypes.func.isRequired,
};

function ReceiptLine({ doc, members }) {
  const receipts = doc.recipients || [];
  const viewedById = {};
  receipts.forEach(r => { if (r.first_viewed_at) viewedById[r.client_user_id] = r; });

  // Who was it addressed to? For a household share that's everyone; for a
  // narrowed one, only the people with a recipient row.
  const audienceIds = doc.audience === 'members'
    ? receipts.map(r => r.client_user_id)
    : members.map(m => m.id);
  const audienceMembers = members.filter(m => audienceIds.includes(m.id));

  const opened = audienceMembers.filter(m => viewedById[m.id]);
  const waiting = audienceMembers.filter(m => !viewedById[m.id]);

  return (
    <div style={{ fontSize: '0.75rem', color: work.muted, marginTop: 2 }}>
      {opened.length > 0 && (
        <span style={{ color: work.green, fontWeight: 600 }}>
          <i className="bi bi-eye-fill me-1" />
          Opened by {opened.map(m => m.first_name || m.email).join(', ')}
        </span>
      )}
      {opened.length > 0 && waiting.length > 0 && <span> · </span>}
      {waiting.length > 0 && (
        <span>Not opened by {waiting.map(m => m.first_name || m.email).join(', ')}</span>
      )}
      {audienceMembers.length === 0 && <span>Nobody in the household can see this.</span>}
    </div>
  );
}
ReceiptLine.propTypes = { doc: PropTypes.object.isRequired, members: PropTypes.array.isRequired };

function SharedFiles({ client }) {
  const { data, refetch, isFetching } = useListClientSharedDocumentsQuery({ id: client.public_id });
  const { data: sharedCatalog } = useGetClientSharedCatalogQuery();
  const [getDownloadUrl] = useGetClientSharedDownloadUrlMutation();
  const [getStreamUrl] = useGetClientSharedStreamUrlMutation();
  const [revoke] = useRevokeClientSharedDocumentMutation();
  const { data: adminConfig } = useGetAdminConfigQuery();
  const showDownloadButtons = adminConfig?.config?.show_download_buttons !== false;

  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [viewer, setViewer] = useState(null);
  const closeViewer = useCallback(() => {
    setViewer(v => {
      if (v?.url && !v.video) URL.revokeObjectURL(v.url);
      return null;
    });
  }, []);

  const documents = data?.documents || [];
  const members = data?.members || [];

  const handleView = async (doc) => {
    setError(null);
    try {
      // A video streams from a playback link; everything else is fetched
      // whole through its one-time link and shown from memory.
      if (isVideoFile(doc.original_filename)) {
        const res = await getStreamUrl({ shared_id: doc.id }).unwrap();
        setViewer({ url: res.url, filename: res.filename, video: true });
        return;
      }
      const res = await getDownloadUrl({ shared_id: doc.id, disposition: 'inline' }).unwrap();
      if (!res.url) throw new Error('No URL returned.');
      setViewer({ url: await fetchAsBlobUrl(res.url), filename: res.filename });
    } catch (e) {
      setError(e?.data?.detail || 'Could not open that file.');
    }
  };

  const handleDownload = async (doc) => {
    setError(null);
    try {
      const res = await getDownloadUrl({ shared_id: doc.id }).unwrap();
      if (!res.url) return;
      // Videos go straight from the one-time link to disk: the response is
      // Content-Disposition: attachment, and a blob would hold up to 2 GB in
      // the tab's memory first.
      const blobUrl = isVideoFile(doc.original_filename) ? null : await fetchAsBlobUrl(res.url);
      const a = document.createElement('a');
      a.href = blobUrl || res.url;
      a.download = res.filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (blobUrl) setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      setError(e?.data?.detail || 'Could not open that file.');
    }
  };

  const doRevoke = async (sharedId) => {
    setError(null);
    try {
      await revoke({ shared_id: sharedId }).unwrap();
      setConfirmRevoke(null);
      refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not un-share that file.');
    }
  };

  return (
    <>
      {/* No `overflow: hidden` — same reason as the requested-documents card
          above: this header's Email the client popover has to be able to hang
          below the card. */}
      <div style={{
        background: work.surface, border: `1px solid ${work.hairline}`,
        borderTop: `3px solid ${work.tealBlue}`,
        borderRadius: 6, marginBottom: '1rem',
      }}>
        <div style={{
          padding: '0.85rem 1rem', borderBottom: `1px solid ${work.hairline}`,
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: work.ink, fontSize: '0.95rem' }}>
              <i className="bi bi-file-earmark-arrow-up me-1" style={{ color: work.tealBlue }} />
              Shared with the client
            </div>
            <div style={{ color: work.muted, fontSize: '0.8rem', marginTop: 2 }}>
              {documents.length === 0
                ? 'Nothing sent yet. Anything shared here appears in their portal.'
                : `${documents.length} document${documents.length === 1 ? '' : 's'} in their portal.`}
            </div>
          </div>
          <NotifySharedButton client={client} documents={documents} onSent={refetch} />
          <button type="button" onClick={() => setOpen(o => !o)} style={{
            background: open ? work.hairline : work.tealBlue,
            color: open ? work.deepBlue : 'white',
            border: 0, borderRadius: 4, padding: '0.45rem 0.9rem',
            fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            {open ? 'Cancel' : <><i className="bi bi-plus-lg me-1" />Share a document</>}
          </button>
        </div>

        {error && (
          <div style={{
            background: 'rgba(176,0,32,0.06)', color: work.red, padding: '0.45rem 1rem',
            fontSize: '0.8rem', borderBottom: `1px solid ${work.hairline}`,
          }}>
            {error}
          </div>
        )}

        {open && (
          <ShareComposer
            client={client}
            members={members}
            catalog={sharedCatalog}
            onError={setError}
            onShared={() => { setOpen(false); refetch(); }}
          />
        )}

        {documents.map((doc, i) => (
          <div key={doc.id} style={{
            padding: '0.6rem 1rem',
            // Last row has no rule: with the card no longer clipping its
            // children, a border here would draw a second line just inside
            // the card's own bottom edge.
            borderBottom: i === documents.length - 1 ? 'none' : `1px solid ${work.hairline}`,
            display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 600, color: work.ink, fontSize: '0.86rem' }}>
                {doc.display_title}
                {doc.document_year && (
                  <span style={{
                    marginLeft: '0.4rem', background: work.canvas, border: `1px solid ${work.hairline}`,
                    borderRadius: 999, padding: '0 0.4rem', fontSize: '0.7rem', color: work.muted, fontWeight: 700,
                  }}>
                    {doc.document_year}
                  </span>
                )}
                {doc.audience === 'members' && (
                  <span style={{ marginLeft: '0.4rem', color: work.goldenrod, fontSize: '0.7rem', fontWeight: 700 }}>
                    <i className="bi bi-person-lock me-1" />Limited
                  </span>
                )}
              </div>
              <div style={{ color: work.muted, fontSize: '0.75rem', marginTop: 1 }}>
                {doc.type_label}
                {doc.shared_by_name && <> · sent by {doc.shared_by_name}</>}
                {doc.created && <> · {formatDate(doc.created)}</>}
                {formatFileSize(doc.file_size_bytes) && <> · {formatFileSize(doc.file_size_bytes)}</>}
                {!doc.notified_at && (
                  <span style={{ color: work.goldenrod, fontWeight: 600 }}> · not emailed</span>
                )}
              </div>
              {doc.note && (
                <div style={{ color: work.ink, fontSize: '0.76rem', marginTop: 3, fontStyle: 'italic' }}>
                  “{doc.note}”
                </div>
              )}
              <ReceiptLine doc={doc} members={members} />
            </div>
            <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
              {/* Office formats can't render in the viewer — the modal's
                  iframe downloads them instead — so the eye is hidden rather
                  than left pretending. The client's portal makes the same
                  distinction; see fileKind() there. */}
              {isVideoFile(doc.original_filename) ? (
                <button type="button" onClick={() => handleView(doc)} title="Watch this video" style={{
                  background: 'transparent', border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                  padding: '0.2rem 0.5rem', color: work.deepBlue, fontSize: '0.75rem', cursor: 'pointer',
                }}>
                  <i className="bi bi-play-fill" />
                </button>
              ) : canPreviewFile(doc.original_filename) ? (
                <button type="button" onClick={() => handleView(doc)} title="Open this file" style={{
                  background: 'transparent', border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                  padding: '0.2rem 0.5rem', color: work.deepBlue, fontSize: '0.75rem', cursor: 'pointer',
                }}>
                  <i className="bi bi-eye" />
                </button>
              ) : (
                <span style={{ color: work.muted, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                  {fileKindLabel(doc.original_filename)} - downloads
                </span>
              )}
              {showDownloadButtons && (
                <button type="button" onClick={() => handleDownload(doc)} title="Download this file" style={{
                  background: 'transparent', border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                  padding: '0.2rem 0.5rem', color: work.deepBlue, fontSize: '0.75rem', cursor: 'pointer',
                }}>
                  <i className="bi bi-download" />
                </button>
              )}
              <button
                type="button" onClick={() => setConfirmRevoke(doc.id)}
                title="Take this away from the client. The file itself is retained."
                style={{
                  background: 'transparent', border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
                  padding: '0.2rem 0.5rem', color: work.red, fontSize: '0.75rem', cursor: 'pointer',
                }}
              >
                <i className="bi bi-x-circle" />
              </button>
            </div>
            {confirmRevoke === doc.id && (
              <div style={{
                flexBasis: '100%', background: 'rgba(176,0,32,0.05)',
                border: `1px solid ${work.hairline}`, borderRadius: 4,
                padding: '0.5rem 0.75rem', fontSize: '0.78rem', color: work.ink,
              }}>
                Un-share <strong>{doc.display_title}</strong>? They lose access immediately.
                The file is retained for compliance and cannot be deleted.
                {(doc.recipients || []).some(r => r.first_viewed_at) && (
                  <div style={{ color: work.goldenrod, marginTop: 3 }}>
                    <i className="bi bi-exclamation-triangle me-1" />
                    Someone has already opened this.
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                  <button type="button" onClick={() => doRevoke(doc.id)} style={{
                    background: work.red, color: 'white', border: 0, borderRadius: 4,
                    padding: '0.3rem 0.7rem', fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer',
                  }}>
                    Un-share
                  </button>
                  <button type="button" onClick={() => setConfirmRevoke(null)} style={{
                    background: 'transparent', color: work.deepBlue, border: `1px solid ${work.hairlineStrong}`,
                    borderRadius: 4, padding: '0.3rem 0.7rem', fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer',
                  }}>
                    Keep it shared
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {isFetching && documents.length === 0 && (
          <div style={{ padding: '0.6rem 1rem', color: work.muted, fontSize: '0.8rem' }}>Loading…</div>
        )}
      </div>
      <DocumentViewerModal url={viewer?.url} filename={viewer?.filename} video={!!viewer?.video} onClose={closeViewer} />
    </>
  );
}
SharedFiles.propTypes = { client: PropTypes.object.isRequired };

// ─── Salesforce documents (the linked AUM client's corpus) ───────────────────
// The other half of this client's paper. Portal uploads are what they have
// sent us and sit in ReceivedFiles above; this is everything already on file
// in Salesforce for the AUM client they are linked to. Two sources, one page,
// each clearly labelled - a single merged list would bury the three documents
// a client just uploaded under a hundred historical ones, and the two are not
// interchangeable (portal uploads are WORM-locked client submissions with
// owner attribution and Textract summaries; these are the migrated book).
//
// Collapsed by default and the tree is mounted only once opened, so an
// ordinary visit to this page doesn't pull a household's whole document list.
// The count in the header comes from the link payload, which already carries
// it, so the closed state still tells you whether it is worth opening.
function SalesforceDocuments({ client, isMobile }) {
  const { data } = useGetClientFscLinkQuery({ id: client.public_id });
  const [open, setOpen] = useState(false);

  // Nothing to show until the household is linked and the caller may see it.
  // The AUM card directly above explains both cases, so this stays silent
  // rather than repeating it.
  if (!data?.linked || data?.visible === false || !data?.link?.fsc_household_id) return null;

  const fscId = data.link.fsc_household_id;
  const name = data.link.household_name;
  const count = data.facts?.doc_count;

  return (
    <div style={{
      background: work.surface, border: `1px solid ${work.hairline}`,
      borderRadius: 6, overflow: 'hidden', marginBottom: '1rem',
    }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%', textAlign: 'left', background: 'transparent', border: 0,
          padding: '0.85rem 1rem', display: 'flex', alignItems: 'center',
          gap: '0.6rem', flexWrap: 'wrap', cursor: 'pointer',
        }}
      >
        <i className={`bi bi-chevron-${open ? 'down' : 'right'}`} style={{ color: work.muted, fontSize: '0.8rem' }} />
        <i className="bi bi-cloud-arrow-down" style={{ color: work.tealBlue }} />
        <span style={{ fontWeight: 700, color: work.ink, fontSize: '0.95rem' }}>
          Salesforce documents
        </span>
        {count != null && (
          <span style={{
            background: work.canvas, border: `1px solid ${work.hairline}`, color: work.ink,
            borderRadius: 999, padding: '0.05rem 0.5rem', fontSize: '0.72rem', fontWeight: 700,
          }}>
            {count.toLocaleString()}
          </span>
        )}
        <span style={{ color: work.muted, fontSize: '0.8rem', flex: 1, minWidth: 0 }}>
          Already on file for {name || 'the linked Salesforce client'}
        </span>
        <Link
          to={`/work/clients/fsc/${fscId}`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: work.tealBlue, fontSize: '0.78rem', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
        >
          Open Salesforce client <i className="bi bi-box-arrow-up-right" />
        </Link>
      </button>

      {open && (
        <div style={{ borderTop: `1px solid ${work.hairline}` }}>
          <FscDocumentTree
            fscHouseholdId={fscId}
            isMobile={isMobile}
            emptyMessage="No documents on file in Salesforce for this client yet."
          />
        </div>
      )}
    </div>
  );
}
SalesforceDocuments.propTypes = {
  client: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
};

// ─── Point people (editable) ─────────────────────────────────────────────────
// Internal staff notified on client uploads/questions from the portal. Same
// inline-edit feel as AdvisorPicker/GemstonePicker, but a multi-value list —
// existing people show as removable chips, and "Add point person" reveals an
// employee type-ahead (against the already-loaded directory) plus a raw-email
// field, mirroring the wizard's PointPeopleCard.
function PointPeopleEditor({ client }) {
  const { data: directory = [] } = useLoadCompanyDirectoryQuery();
  const [addPointPerson] = useAddClientPointPersonMutation();
  const [removePointPerson] = useRemoveClientPointPersonMutation();
  // Hidden behind a pencil icon by default — the list is read-only until the
  // operator explicitly asks to edit it.
  const [editing, setEditing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [query, setQuery] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const pointPeople = client.point_people || [];
  const existingDirectoryIds = new Set(pointPeople.filter(p => p.kind === 'employee').map(p => p.directory_id));
  const existingEmails = new Set(pointPeople.map(p => (p.email || '').toLowerCase()));

  const q = query.trim().toLowerCase();
  const matches = q
    ? directory
        .filter(d => d.is_active !== false && !existingDirectoryIds.has(d.id))
        .filter(d => {
          const name = `${d.first_name || ''} ${d.last_name || ''}`.toLowerCase();
          return name.includes(q) || (d.final_email || '').toLowerCase().includes(q);
        })
        .slice(0, 8)
    : [];

  const pickEmployee = async (d) => {
    setError(null);
    setBusy(true);
    try {
      await addPointPerson({ id: client.public_id, kind: 'employee', directory_id: d.id }).unwrap();
      setQuery('');
    } catch (e) {
      setError(e?.data?.detail || 'Could not add that person.');
    } finally {
      setBusy(false);
    }
  };

  const addEmail = async () => {
    const email = emailDraft.trim().toLowerCase();
    if (!email || !email.includes('@')) { setError('Enter a valid email.'); return; }
    if (existingEmails.has(email)) { setError('Already added.'); return; }
    setError(null);
    setBusy(true);
    try {
      await addPointPerson({ id: client.public_id, kind: 'email', email }).unwrap();
      setEmailDraft('');
    } catch (e) {
      setError(e?.data?.detail || 'Could not add that email.');
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (p) => {
    setError(null);
    try {
      await removePointPerson({ id: client.public_id, point_person_id: p.id }).unwrap();
    } catch (e) {
      setError(e?.data?.detail || 'Could not remove that person.');
    }
  };

  const toggleEditing = () => {
    setEditing(v => {
      const next = !v;
      if (!next) { setShowAdd(false); setQuery(''); setEmailDraft(''); setError(null); }
      return next;
    });
  };

  const inputStyle = {
    width: '100%', border: `1px solid ${work.hairline}`, borderRadius: 4,
    padding: '0.3rem 0.5rem', fontSize: '0.82rem', color: work.ink,
    background: 'white', outline: 'none',
  };
  const linkBtnStyle = {
    background: 'transparent', border: 0, padding: 0,
    color: work.tealBlue, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
  };

  return (
    <div style={{ marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' }}>
      <span style={{
        color: work.muted, fontSize: '0.72rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', width: 96, flexShrink: 0, marginTop: 3,
      }}>
        Point people
      </span>
      <div style={{ flex: 1, minWidth: 220, maxWidth: 340 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
          {pointPeople.length === 0 && !editing && (
            <span style={{ color: work.muted, fontSize: '0.8rem', fontStyle: 'italic' }}>None set</span>
          )}
          {pointPeople.map(p => (
            <span
              key={p.id}
              title={p.email}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                background: work.canvas, border: `1px solid ${work.hairline}`, borderRadius: 4,
                padding: '0.2rem 0.4rem 0.2rem 0.5rem', fontSize: '0.8rem', color: work.ink,
              }}
            >
              <i className={`bi ${p.kind === 'employee' ? 'bi-person-badge' : 'bi-envelope'}`} />
              {p.kind === 'employee' ? p.name : p.email}
              {editing && (
                <button
                  type="button"
                  onClick={() => removeOne(p)}
                  aria-label={`Remove ${p.kind === 'employee' ? p.name : p.email}`}
                  style={{ background: 'none', border: 0, padding: 0, marginLeft: 2, color: work.muted, cursor: 'pointer', lineHeight: 1, display: 'inline-flex' }}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: '0.7rem' }} />
                </button>
              )}
            </span>
          ))}
          <button
            type="button"
            onClick={toggleEditing}
            aria-label={editing ? 'Stop editing point people' : 'Edit point people'}
            title={editing ? 'Stop editing' : 'Edit point people'}
            style={{ background: 'none', border: 0, padding: 0, color: editing ? work.tealBlue : work.muted, cursor: 'pointer', lineHeight: 1, display: 'inline-flex' }}
          >
            <i className="bi bi-pencil-fill" style={{ fontSize: '0.72rem' }} />
          </button>
        </div>

        {editing && !showAdd && (
          <button type="button" onClick={() => setShowAdd(true)} style={{ ...linkBtnStyle, marginTop: '0.5rem', display: 'block' }}>
            <i className="bi bi-plus-lg me-1" />Add point person
          </button>
        )}

        {editing && showAdd && (
          <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search employees by name or email…"
                disabled={busy}
                style={inputStyle}
              />
              {q && (
                <div style={{
                  position: 'absolute', zIndex: 5, top: '100%', left: 0, right: 0, marginTop: 2,
                  background: 'white', border: `1px solid ${work.hairline}`, borderRadius: 4,
                  boxShadow: '0 4px 10px rgba(10,41,71,0.12)', maxHeight: 200, overflowY: 'auto',
                }}>
                  {matches.length === 0 ? (
                    <div style={{ padding: '0.5rem 0.6rem', color: work.muted, fontSize: '0.78rem' }}>No matches.</div>
                  ) : matches.map(d => (
                    <div
                      key={d.id}
                      onMouseDown={() => pickEmployee(d)}
                      style={{ padding: '0.4rem 0.6rem', cursor: 'pointer', borderBottom: `1px solid ${work.hairline}` }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = work.canvas; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '0.8rem', color: work.ink }}>{`${d.first_name || ''} ${d.last_name || ''}`.trim()}</div>
                      <div style={{ fontSize: '0.7rem', color: work.muted }}>{d.final_email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <input
                type="email"
                value={emailDraft}
                onChange={(e) => { setEmailDraft(e.target.value); setError(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                placeholder="or add an email address"
                disabled={busy}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                type="button"
                onClick={addEmail}
                disabled={busy || !emailDraft.trim()}
                style={{
                  background: (busy || !emailDraft.trim()) ? work.hairline : work.tealBlue,
                  color: (busy || !emailDraft.trim()) ? work.muted : 'white',
                  border: 0, borderRadius: 4, padding: '0 0.7rem', fontWeight: 700, fontSize: '0.8rem',
                  cursor: (busy || !emailDraft.trim()) ? 'not-allowed' : 'pointer',
                }}
              >
                Add
              </button>
            </div>
            <button
              type="button"
              onClick={() => { setShowAdd(false); setQuery(''); setEmailDraft(''); setError(null); }}
              style={{ ...linkBtnStyle, color: work.muted, alignSelf: 'flex-start' }}
            >
              Done
            </button>
          </div>
        )}
        {error && <div style={{ color: work.red, fontSize: '0.72rem', marginTop: 4 }}>{error}</div>}
      </div>
    </div>
  );
}
PointPeopleEditor.propTypes = {
  client: PropTypes.object.isRequired,
};

// ─── DocuSign agreement status + advisor signing ────────────────────────────
const AGREEMENT_STATUS = {
  sent:      { label: 'Awaiting signatures',       color: work.goldenrod, icon: 'hourglass-split' },
  delivered: { label: 'Opened — awaiting signing', color: work.goldenrod, icon: 'envelope-open' },
  completed: { label: 'Completed',                 color: work.green,     icon: 'check-circle-fill' },
  declined:  { label: 'Declined',                  color: work.red,       icon: 'x-circle' },
  voided:    { label: 'Voided',                    color: work.muted,     icon: 'slash-circle' },
  missing:   { label: 'Not found on DocuSign — re-send', color: work.red, icon: 'exclamation-triangle' },
};

function AgreementSection({ client }) {
  const feeCents = client.onetime_fee_cents || 0;
  const { data, isFetching, refetch } = useGetClientAgreementQuery({ id: client.public_id });
  const [send, { isLoading: isSending }] = useSendClientAgreementMutation();
  const [getUrl, { isLoading: isOpening }] = useGetAdvisorSigningUrlMutation();
  const [error, setError] = useState(null);

  const agreement = data?.agreement || null;
  // No fee and nothing sent → no agreement is relevant for this client.
  if (!feeCents && !agreement) return null;

  const dollars = (feeCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const meta = agreement ? (AGREEMENT_STATUS[agreement.status] || { label: agreement.status, color: work.muted, icon: 'file-earmark-text' }) : null;
  // Actionable = still out for signature. A missing/declined/voided/completed
  // envelope has no live signing session, so don't offer a sign button (it 502s).
  const actionable = agreement && ['sent', 'delivered'].includes(agreement.status);
  // No agreement yet, or the last one is gone from DocuSign → let the advisor
  // (re)send. A fresh envelope supersedes the missing one.
  const canSend = feeCents > 0 && (!agreement || agreement.status === 'missing');

  const onSend = async () => {
    setError(null);
    try { await send({ id: client.public_id }).unwrap(); refetch(); }
    catch (e) { setError(e?.data?.detail || 'Could not send the agreement.'); }
  };
  const onSign = async () => {
    setError(null);
    try {
      const { url } = await getUrl({ id: client.public_id, return_url: window.location.href }).unwrap();
      if (url) window.location.assign(url);
    } catch (e) { setError(e?.data?.detail || 'Could not open the signing session.'); }
  };

  return (
    <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6, padding: '0.9rem 1.1rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 800, color: work.deepBlue, fontSize: '0.95rem' }}>
            <i className="bi bi-vector-pen me-1" /> Consulting Agreement
          </div>
          <div style={{ color: work.muted, fontSize: '0.8rem', marginTop: 2 }}>
            One-time fee ${dollars}
            {meta && <> · <span style={{ color: meta.color, fontWeight: 700 }}><i className={`bi bi-${meta.icon} me-1`} />{meta.label}</span></>}
            {isFetching && <span style={{ marginLeft: '0.4rem', opacity: 0.6 }}>…</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {canSend && (
            <button type="button" onClick={onSend} disabled={isSending} style={{ background: work.goldenrod, color: work.deepBlue, border: 0, borderRadius: 4, padding: '0.45rem 0.9rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              <i className="bi bi-send me-1" />{isSending ? 'Sending…' : (agreement ? 'Re-send for signature' : 'Send for signature')}
            </button>
          )}
          {actionable && (
            <button type="button" onClick={onSign} disabled={isOpening} style={{ background: work.tealBlue, color: 'white', border: 0, borderRadius: 4, padding: '0.45rem 0.9rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              <i className="bi bi-pen me-1" />{isOpening ? 'Opening…' : 'Review & sign as advisor'}
            </button>
          )}
        </div>
      </div>
      {error && <div style={{ color: work.red, fontSize: '0.8rem', marginTop: '0.5rem' }}>{error}</div>}
    </div>
  );
}
AgreementSection.propTypes = { client: PropTypes.object.isRequired };

// ─── Files received from the client (with Textract summaries) ───────────────
//
// Renders only when at least one upload has landed. Groups uploads by the
// requested category so the advisor sees them in the same shape they
// asked for. Each upload surfaces its Textract-derived summary fields
// inline (institution, account last-4, balance, statement period) and a
// Download button that hits the presigned-URL endpoint.
function ReceivedFiles({ client, catalog, devMode = false }) {
  const [getDownloadUrl] = useGetClientUploadDownloadUrlMutation();
  const [reExtract, { isLoading: isReExtracting }] = useReExtractClientUploadMutation();
  const [setOwner] = useSetClientUploadOwnerMutation();
  const { data: adminConfig } = useGetAdminConfigQuery();
  const showDownloadButtons = adminConfig?.config?.show_download_buttons !== false;
  const [downloadError, setDownloadError] = useState(null);
  const [reExtractingId, setReExtractingId] = useState(null);
  // In-app viewer state — {url: blob:..., filename} or null. Rendered via
  // DocumentViewerModal, an <iframe> over the current page instead of a new
  // browser tab, so viewing a document never leaves the authenticated app.
  const [viewer, setViewer] = useState(null);
  const closeViewer = useCallback(() => {
    setViewer(v => {
      if (v?.url) URL.revokeObjectURL(v.url);
      return null;
    });
  }, []);

  // Household roster drives owner attribution. Only worth showing the
  // control when there's more than one person a file could belong to.
  const members = client?.household_members || [];
  const memberById = {};
  members.forEach(m => { memberById[m.id] = m; });
  const showOwner = members.length >= 2;

  const handleSetOwner = async (uploadId, selection) => {
    setDownloadError(null);
    try {
      await setOwner({ upload_id: uploadId, client_id: client?.public_id, ...selection }).unwrap();
    } catch (e) {
      setDownloadError(e?.data?.detail || 'Could not update who this file is for.');
    }
  };

  const requestsWithUploads = (client?.requested_documents || [])
    .filter(r => (r.uploads?.length || 0) > 0);

  if (requestsWithUploads.length === 0) return null;

  // Build a label lookup from the catalog so we render friendly names.
  const labelByKey = {};
  (catalog || []).forEach(cat => (cat.items || []).forEach(it => { labelByKey[it.key] = it.label; }));

  const handleDownload = async (uploadId) => {
    setDownloadError(null);
    try {
      const res = await getDownloadUrl({ upload_id: uploadId }).unwrap();
      if (!res.url) return;
      // Fetch the one-time link ourselves and hand the browser a blob: URL —
      // see fetchAsBlobUrl's comment for why we never navigate to it
      // directly. A hidden <a download> reliably forces a save dialog.
      const blobUrl = await fetchAsBlobUrl(res.url);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = res.filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      setDownloadError(e?.data?.detail || 'Could not open that file.');
    }
  };

  // "View" shows the file in an in-app modal (DocumentViewerModal), vs.
  // "Download" which forces a save. Fetching the one-time link ourselves and
  // handing the iframe a blob: URL — rather than a new browser tab pointed at
  // a real URL — keeps the document behind the app's own session for as long
  // as it's on screen; see fetchAsBlobUrl's comment for the underlying reason
  // we never navigate anywhere to the one-time link directly.
  const handleView = async (uploadId) => {
    setDownloadError(null);
    try {
      const res = await getDownloadUrl({ upload_id: uploadId, disposition: 'inline' }).unwrap();
      if (!res.url) throw new Error('No URL returned.');
      const blobUrl = await fetchAsBlobUrl(res.url);
      setViewer({ url: blobUrl, filename: res.filename });
    } catch (e) {
      setDownloadError(e?.data?.detail || 'Could not open that file.');
    }
  };

  const handleReExtract = async (uploadId) => {
    setDownloadError(null);
    setReExtractingId(uploadId);
    try {
      await reExtract({ upload_id: uploadId, client_id: client?.id }).unwrap();
    } catch (e) {
      setDownloadError(e?.data?.detail || 'Could not re-run extraction on that file.');
    } finally {
      setReExtractingId(null);
    }
  };

  const totalFiles = requestsWithUploads.reduce((sum, r) => sum + r.uploads.length, 0);

  return (
    <>
    <div style={{
      background: work.surface, border: `1px solid ${work.hairline}`,
      borderTop: `3px solid ${work.green}`,
      borderRadius: 6, overflow: 'hidden', marginBottom: '1rem',
    }}>
      <div style={{ padding: '0.85rem 1rem', borderBottom: `1px solid ${work.hairline}` }}>
        <div style={{ fontWeight: 700, color: work.ink, fontSize: '0.95rem' }}>
          <i className="bi bi-file-earmark-arrow-down me-1" style={{ color: work.green }} />
          Files received
        </div>
        <div style={{ color: work.muted, fontSize: '0.8rem', marginTop: 2 }}>
          {totalFiles} file{totalFiles === 1 ? '' : 's'} across {requestsWithUploads.length} categor{requestsWithUploads.length === 1 ? 'y' : 'ies'}. 
        </div>
      </div>

      {downloadError && (
        <div style={{ background: 'rgba(176,0,32,0.06)', color: work.red, padding: '0.45rem 1rem', fontSize: '0.8rem', borderBottom: `1px solid ${work.hairline}` }}>
          {downloadError}
        </div>
      )}

      <div>
        {requestsWithUploads.map(r => (
          <div key={r.id} style={{ borderBottom: `1px solid ${work.hairline}` }}>
            <div style={{ padding: '0.6rem 1rem', background: 'rgba(0,105,166,0.04)', fontSize: '0.78rem', fontWeight: 700, color: work.deepBlue, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {r.custom_label || labelByKey[r.type_key] || r.type_key}
              <span style={{ color: work.muted, marginLeft: '0.5rem', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
                · {r.uploads.length} file{r.uploads.length === 1 ? '' : 's'}
              </span>
            </div>
            {r.uploads.map(u => (
              <UploadSummaryRow
                key={u.id}
                upload={u}
                onDownload={showDownloadButtons ? () => handleDownload(u.id) : undefined}
                onView={() => handleView(u.id)}
                onReExtract={devMode ? () => handleReExtract(u.id) : undefined}
                isReExtracting={isReExtracting && reExtractingId === u.id}
                showOwner={showOwner}
                members={members}
                memberById={memberById}
                onSetOwner={handleSetOwner}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
    <DocumentViewerModal url={viewer?.url} filename={viewer?.filename} onClose={closeViewer} />
    </>
  );
}
ReceivedFiles.propTypes = { client: PropTypes.object, catalog: PropTypes.array, devMode: PropTypes.bool };

// Liquidity time-horizon buckets for the advisor's "now / soon / later" plan.
// Employee-page only — the client-facing portal never receives this analysis
// (stripped server-side in /portal/me).
const BUCKET_META = {
  now:   { label: 'Now',   sub: 'Accessible now',            color: work.green },
  soon:  { label: 'Soon',  sub: 'Accessible soon, low cost', color: work.goldenrod },
  later: { label: 'Later', sub: 'Tied up for later / heirs', color: work.tealBlue },
};
const BUCKET_ORDER = ['now', 'soon', 'later'];

function LiquidityBuckets({ analysis }) {
  const holdings = Array.isArray(analysis?.holdings) ? analysis.holdings : [];
  const grouped = { now: [], soon: [], later: [], other: [] };
  holdings.forEach((h) => {
    grouped[BUCKET_ORDER.includes(h.bucket) ? h.bucket : 'other'].push(h);
  });

  const Group = ({ meta, items }) => (
    <div style={{ borderLeft: `3px solid ${meta.color}`, paddingLeft: '0.6rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', marginBottom: '0.2rem' }}>
        <span style={{ color: meta.color, fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {meta.label}
        </span>
        {meta.sub && <span style={{ color: work.muted, fontSize: '0.65rem' }}>{meta.sub}</span>}
      </div>
      {items.map((h, i) => (
        <div key={i} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0.45rem', fontSize: '0.78rem', marginBottom: '0.15rem' }}>
          <span style={{ color: work.ink, fontWeight: 600 }}>{h.account_or_product || h.account_type || 'Holding'}</span>
          {h.account_type && h.account_or_product && <span style={{ color: work.muted, fontSize: '0.7rem' }}>{h.account_type}</span>}
          {h.value && <span style={{ color: work.deepBlue, fontWeight: 700 }}>{h.value}</span>}
          {h.liquidity_notes && <span style={{ color: work.muted, fontSize: '0.7rem', fontStyle: 'italic' }}>— {h.liquidity_notes}</span>}
        </div>
      ))}
    </div>
  );
  Group.propTypes = { meta: PropTypes.object, items: PropTypes.array };

  return (
    <div style={{ paddingLeft: '1.4rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0.5rem' }}>
        <span style={{ color: work.muted, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
          Liquidity plan
        </span>
        {analysis.document_type && <span style={{ color: work.ink, fontSize: '0.75rem', fontWeight: 600 }}>{analysis.document_type}</span>}
        {analysis.summary && <span style={{ color: work.muted, fontSize: '0.75rem' }}>{analysis.summary}</span>}
      </div>
      {holdings.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
          {BUCKET_ORDER.map((b) => grouped[b].length > 0 && (
            <Group key={b} meta={BUCKET_META[b]} items={grouped[b]} />
          ))}
          {grouped.other.length > 0 && (
            <Group meta={{ label: 'Unclassified', sub: '', color: work.muted }} items={grouped.other} />
          )}
        </div>
      ) : (
        <span style={{ color: work.muted, fontSize: '0.72rem', fontStyle: 'italic' }}>
          No investable holdings detected in this document.
        </span>
      )}
    </div>
  );
}
LiquidityBuckets.propTypes = { analysis: PropTypes.object.isRequired };

function memberLabel(m) {
  if (!m) return 'Unknown';
  const name = `${m.first_name || ''} ${m.last_name || ''}`.trim();
  return name || m.email || 'Client';
}

const CONF_COLOR = { high: work.green, medium: work.goldenrod, low: work.muted };

// Advisor-facing "who is this file for" control. Shows the AI's best guess
// (member / Joint / Unknown) with a confidence dot, and a dropdown to
// override it. The override wins over the guess; picking "Auto" clears it.
function OwnerControl({ upload, members, memberById, onSetOwner }) {
  const guessKind = upload.owner_guess_kind || null;      // null = not analyzed yet
  const overrideKind = upload.owner_override_kind || null;
  const isOverridden = !!overrideKind;
  const effKind = overrideKind || guessKind;
  const effClientId = isOverridden ? upload.owner_override_client_id : upload.owner_guess_client_id;

  // Current <select> value.
  let value = 'unknown';
  if (effKind === 'member' && effClientId != null) value = `m:${memberById[effClientId]?.public_id || ''}`;
  else if (effKind === 'joint') value = 'joint';
  else if (effKind === 'unknown') value = 'unknown';

  const onChange = (e) => {
    const v = e.target.value;
    if (v === 'auto') onSetOwner(upload.id, { kind: 'auto' });
    else if (v === 'joint') onSetOwner(upload.id, { kind: 'joint' });
    else if (v === 'unknown') onSetOwner(upload.id, { kind: 'unknown' });
    else if (v.startsWith('m:')) onSetOwner(upload.id, { kind: 'member', client_public_id: v.slice(2) });
  };

  // While extraction is still running we have no guess yet.
  const pending = !guessKind && !overrideKind;
  const guessName = guessKind === 'member'
    ? memberLabel(memberById[upload.owner_guess_client_id])
    : guessKind === 'joint' ? 'Joint' : guessKind === 'unknown' ? 'Unknown' : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', paddingLeft: '1.4rem' }}>
      <span style={{ color: work.muted, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
        For
      </span>
      {pending ? (
        <span style={{ color: work.muted, fontSize: '0.75rem', fontStyle: 'italic' }}>
          determining…
        </span>
      ) : (
        <>
          {/* Confidence dot reflects the AI guess; muted grey once overridden. */}
          <span title={isOverridden ? 'Set by advisor' : `AI confidence: ${upload.owner_guess_confidence || 'low'}`} style={{
            width: 8, height: 8, borderRadius: '50%',
            background: isOverridden ? work.hairlineStrong : (CONF_COLOR[upload.owner_guess_confidence] || work.muted),
            flexShrink: 0,
          }} />
          <select value={value} onChange={onChange} style={{
            border: `1px solid ${work.hairline}`, borderRadius: 4,
            padding: '0.15rem 0.4rem', fontSize: '0.78rem', color: work.ink,
            background: 'white', cursor: 'pointer', maxWidth: 200,
          }}>
            {members.map(m => (
              <option key={m.public_id} value={`m:${m.public_id}`}>{memberLabel(m)}</option>
            ))}
            <option value="joint">Joint</option>
            <option value="unknown">Unknown</option>
          </select>
          {isOverridden ? (
            <button type="button" onClick={() => onSetOwner(upload.id, { kind: 'auto' })} style={{
              background: 'transparent', border: 0, color: work.tealBlue,
              fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', padding: 0,
            }} title={guessName ? `AI suggested: ${guessName}` : 'Revert to AI guess'}>
              revert to AI
            </button>
          ) : (
            <span style={{ color: work.muted, fontSize: '0.68rem', fontStyle: 'italic' }}>AI guess</span>
          )}
        </>
      )}
    </div>
  );
}
OwnerControl.propTypes = {
  upload: PropTypes.object.isRequired,
  members: PropTypes.array.isRequired,
  memberById: PropTypes.object.isRequired,
  onSetOwner: PropTypes.func.isRequired,
};

function UploadSummaryRow({ upload, onDownload, onView, onReExtract, isReExtracting, showOwner, members, memberById, onSetOwner }) {
  const x = upload.textract_extract_json || {};
  const status = upload.textract_status;
  const isProcessing = status === 'pending' || status === 'running' || isReExtracting;
  const isDeferred = status === 'deferred';
  const isSkipped = status === 'skipped';
  const isFailed = status === 'failed';

  // Compact key/value chip
  const Chip = ({ label, value }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'baseline', gap: '0.3rem',
      background: 'rgba(10,41,71,0.04)',
      border: `1px solid ${work.hairline}`,
      borderRadius: 4,
      padding: '0.2rem 0.5rem',
      fontSize: '0.75rem',
      whiteSpace: 'nowrap',
    }}>
      <span style={{ color: work.muted, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>{label}</span>
      <span style={{ color: work.deepBlue, fontWeight: 600 }}>{value}</span>
    </span>
  );
  Chip.propTypes = { label: PropTypes.string, value: PropTypes.node };

  return (
    <div style={{ padding: '0.7rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
        <i className="bi bi-file-earmark-text" style={{ color: work.muted }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: work.ink, fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {upload.display_label || upload.original_filename || 'Uploaded file'}
          </div>
          <div style={{ color: work.muted, fontSize: '0.7rem', marginTop: 1, display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
            {upload.original_filename && upload.original_filename !== upload.display_label && (
              <span>{upload.original_filename}</span>
            )}
            {upload.file_size_bytes != null && <span>{Math.round(upload.file_size_bytes / 1024)} KB</span>}
            {upload.created && <span>uploaded {new Date(upload.created).toLocaleString()}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
          {onReExtract && (
            <button
              type="button"
              onClick={onReExtract}
              disabled={isReExtracting}
              title="Re-run Textract on this file"
              style={{
                background: 'transparent',
                color: isReExtracting ? work.muted : work.goldenrod,
                border: `1px solid ${work.hairlineStrong}`,
                borderRadius: 4, padding: '0.35rem 0.7rem', fontWeight: 700, fontSize: '0.72rem',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                cursor: isReExtracting ? 'wait' : 'pointer', whiteSpace: 'nowrap',
              }}
            >
              <i className={`bi ${isReExtracting ? 'bi-arrow-repeat' : 'bi-magic'} me-1`} />
              {isReExtracting ? 'Working…' : 'Re-extract'}
            </button>
          )}
          {/* Only offered for formats the viewer can render — an Office file
              would download instead, which Download already does honestly. */}
          {onView && canPreviewFile(upload.original_filename) && (
            <button type="button" onClick={onView} title="Open this file" style={{
              background: 'transparent', color: work.tealBlue, border: `1px solid ${work.tealBlue}`,
              borderRadius: 4, padding: '0.35rem 0.7rem', fontWeight: 700, fontSize: '0.72rem',
              textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              <i className="bi bi-eye me-1" /> View
            </button>
          )}
          {!canPreviewFile(upload.original_filename) && (
            <span style={{ color: work.muted, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
              {fileKindLabel(upload.original_filename)} - downloads
            </span>
          )}
          {onDownload && (
            <button type="button" onClick={onDownload} title="Download this file" aria-label="Download this file" style={{
              background: work.tealBlue, color: 'white', border: 'none',
              borderRadius: 4, padding: '0.35rem 0.6rem', fontWeight: 700, fontSize: '0.85rem',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              <i className="bi bi-download" />
            </button>
          )}
        </div>
      </div>

      {/* Household owner attribution — AI guess + advisor override. Only
          shown for multi-member households (nothing to disambiguate solo). */}
      {showOwner && (
        <OwnerControl upload={upload} members={members} memberById={memberById} onSetOwner={onSetOwner} />
      )}

      {/* Textract summary chips — only render when we have something to show */}
      {(x.institution || x.account_number_last4 || x.account_holder || x.account_type || x.total_balance || x.statement_period || x.statement_date) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', paddingLeft: '1.4rem' }}>
          {x.institution        && <Chip label="Institution" value={x.institution} />}
          {x.account_type       && <Chip label="Type" value={x.account_type} />}
          {x.account_number_last4 && <Chip label="Acct" value={`…${x.account_number_last4}`} />}
          {x.account_holder     && <Chip label="Holder" value={x.account_holder} />}
          {x.total_balance      && <Chip label="Balance" value={x.total_balance} />}
          {(x.statement_period || x.statement_date) && (
            <Chip label="Period" value={x.statement_period || x.statement_date} />
          )}
        </div>
      )}

      {/* Auto-surfaced "Total *" rows from the statement. Subdued so the
          curated chips above stay the primary read; capped to 4 server-side. */}
      {Array.isArray(x.notable_totals) && x.notable_totals.length > 0 && (
        <div style={{ paddingLeft: '1.4rem', display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0.4rem' }}>
          <span style={{ color: work.muted, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
            Totals from statement
          </span>
          {x.notable_totals.map((t, i) => (
            <span key={`${t.key}-${i}`} style={{
              display: 'inline-flex', alignItems: 'baseline', gap: '0.3rem',
              background: 'transparent',
              border: `1px dashed ${work.hairline}`,
              borderRadius: 4,
              padding: '0.15rem 0.45rem',
              fontSize: '0.7rem',
              whiteSpace: 'nowrap',
              color: work.muted,
            }}>
              <span>{t.key}</span>
              <span style={{ color: work.ink, fontWeight: 600 }}>{t.value}</span>
            </span>
          ))}
        </div>
      )}

      {/* Advisor-only liquidity bucketing (now / soon / later). Present only
          when the Bedrock analysis ran; stripped from the client portal payload. */}
      {x.analysis && <LiquidityBuckets analysis={x.analysis} />}

      {/* Status hints for cases where we don't have extracted fields yet.
          'running' is the in-flight async path (PDFs); 'pending' is the
          brief window between row insert and the sync extractor finishing.
          'deferred' is now legacy (kept for old rows) but reads the same. */}
      {!x.institution && (isProcessing || isDeferred || isSkipped || isFailed) && (
        <div style={{
          paddingLeft: '1.4rem',
          color: isFailed ? work.red : work.muted,
          fontSize: '0.72rem',
          fontStyle: 'italic',
        }}>
          {isProcessing && (isReExtracting
            ? 'Re-running extraction — results will refresh shortly.'
            : 'Reading file — extract will appear in a minute or two.')}
          {isDeferred  && (x.error || 'Waiting on the async extractor.')}
          {isSkipped   && (x.error || 'Extraction not run on this file type.')}
          {isFailed    && (x.error || 'Couldn’t extract data from this file.')}
        </div>
      )}
    </div>
  );
}
UploadSummaryRow.propTypes = {
  upload: PropTypes.object.isRequired,
  onDownload: PropTypes.func,
  onView: PropTypes.func,
  onReExtract: PropTypes.func,
  isReExtracting: PropTypes.bool,
  showOwner: PropTypes.bool,
  members: PropTypes.array,
  memberById: PropTypes.object,
  onSetOwner: PropTypes.func,
};
